import { api }             from './api.js';
import { renderPulse }     from './pulse.js';
import { renderPlan, hideDayDrawer } from './plan.js';
import { renderProgress }  from './progress.js';
import { renderSettings }  from './settings.js';
import { startOnboarding } from './onboarding.js';
import { fmt$, esc, closeSheet, fmtDate, getWeekBounds, priorityBadge, getWakingSegments, matchesTimePreference, showToast, presentSheet} from './utils.js';

// ── SCREENS ───────────────────────────────────────────────────────────────

const authScreen        = document.getElementById('auth-screen');
const onboardingScreen  = document.getElementById('onboarding-screen');
const appShell          = document.getElementById('app-shell');
const fab               = document.getElementById('fab');
const sidebarQuickAdd   = document.getElementById('sidebar-quick-add');
const birthdayOverlay   = document.getElementById('birthday-overlay');
const annualLetterOverlay = document.getElementById('annual-letter-overlay');

// ── AUTH FORM ─────────────────────────────────────────────────────────────

let authMode    = 'signin';
// Sign-up is a 3-step flow gated on email verification — 'email' (enter +
// send code) -> 'code' (enter the 6-digit code) -> 'details' (name/password,
// the actual account-creation submit). The server enforces this too (see
// src/lib/auth.ts's hooks.before) — this state machine is UX, not the real
// gate, so a client bypassed straight to signUp() would still be rejected.
let signupStep         = 'email';
let verifiedSignupEmail = null;

function showAuthError(err) {
  console.error('[auth] error:', err);
  const errEl = document.getElementById('auth-error');
  errEl.textContent = err?.message || (typeof err === 'string' ? err : null) || 'Something went wrong.';
  errEl.classList.add('show');
}

function updateSignupStepUI() {
  const nameGroup    = document.getElementById('name-group');
  const emailInput   = document.getElementById('auth-email');
  const verifiedNote = document.getElementById('signup-email-verified-note');
  const codeGroup    = document.getElementById('signup-code-group');
  const pwGroup      = document.getElementById('password-group');
  const pwInput      = document.getElementById('auth-password');
  const confirmGroup = document.getElementById('confirm-group');
  const confirmInput = document.getElementById('auth-confirm');
  const pwStrength   = document.getElementById('pw-strength');
  const btnLabel     = document.getElementById('auth-btn-label');

  if (authMode !== 'signup') {
    nameGroup.classList.add('hidden');
    codeGroup.classList.add('hidden');
    confirmGroup.classList.add('hidden');
    pwStrength.classList.add('hidden');
    verifiedNote.classList.add('hidden');
    pwGroup.classList.remove('hidden');
    pwInput.required = true;
    confirmInput.required = false;
    emailInput.disabled = false;
    btnLabel.textContent = 'Sign in';
    return;
  }

  const atDetails = signupStep === 'details';
  nameGroup.classList.toggle('hidden', !atDetails);
  confirmGroup.classList.toggle('hidden', !atDetails);
  pwStrength.classList.toggle('hidden', !atDetails);
  pwGroup.classList.toggle('hidden', !atDetails);
  pwInput.required = atDetails;
  confirmInput.required = atDetails;
  codeGroup.classList.toggle('hidden', signupStep !== 'code');
  verifiedNote.classList.toggle('hidden', !atDetails);
  emailInput.disabled = signupStep !== 'email';

  btnLabel.textContent =
    signupStep === 'email' ? 'Send verification code' :
    signupStep === 'code'  ? 'Verify code' :
    'Create account';
}

document.querySelectorAll('.auth-tab').forEach(btn => {
  btn.addEventListener('click', () => {
    authMode = btn.dataset.tab;
    signupStep = 'email';
    verifiedSignupEmail = null;
    document.getElementById('signup-otp').value = '';
    document.querySelectorAll('.auth-tab').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    document.getElementById('forgot-link').classList.toggle('hidden', authMode !== 'signin');
    document.getElementById('auth-error').classList.remove('show');
    document.getElementById('auth-password').setAttribute('autocomplete',
      authMode === 'signin' ? 'current-password' : 'new-password');
    updateSignupStepUI();
  });
});

document.getElementById('signup-change-email').addEventListener('click', (e) => {
  e.preventDefault();
  signupStep = 'email';
  verifiedSignupEmail = null;
  document.getElementById('signup-otp').value = '';
  document.getElementById('auth-error').classList.remove('show');
  updateSignupStepUI();
  document.getElementById('auth-email').focus();
});

document.getElementById('signup-resend-otp').addEventListener('click', async (e) => {
  e.preventDefault();
  const email = document.getElementById('auth-email').value.trim();
  if (!email) return;
  try {
    await api.auth.sendSignupOtp(email);
    showToast('Code resent');
  } catch (err) {
    showAuthError(err);
  }
});

// Password strength (signup details step only)
document.getElementById('auth-password').addEventListener('input', (e) => {
  if (authMode !== 'signup' || signupStep !== 'details') return;
  const pw    = e.target.value;
  const score = calcPasswordStrength(pw);
  const bars  = document.querySelectorAll('.pw-bar');
  const colors = ['var(--negative)', 'var(--caution)', 'var(--caution)', 'var(--positive)'];
  const labels = ['Too short', 'Weak', 'Fair', 'Strong'];
  bars.forEach((bar, i) => {
    bar.style.background = i < score ? colors[score - 1] : 'var(--bg-hover)';
  });
  document.getElementById('pw-strength-label').textContent = pw.length >= 12 ? labels[score - 1] : 'Min. 12 characters';
  document.getElementById('pw-strength-label').style.color =
    score >= 3 ? 'var(--positive)' : score >= 2 ? 'var(--caution)' : 'var(--negative)';
});

function calcPasswordStrength(pw) {
  let score = 0;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.max(1, Math.min(4, score));
}

document.getElementById('auth-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const errEl = document.getElementById('auth-error');
  const email = document.getElementById('auth-email').value.trim();
  errEl.classList.remove('show');
  const btn = document.getElementById('auth-btn-label');
  const btnTextBeforeSubmit = btn.textContent;

  if (authMode === 'signin') {
    const password = document.getElementById('auth-password').value;
    btn.textContent = '…';
    try {
      await api.auth.signIn(email, password);
      await afterAuth();
    } catch (err) {
      showAuthError(err);
      btn.textContent = btnTextBeforeSubmit;
    }
    return;
  }

  // authMode === 'signup'
  if (signupStep === 'email') {
    btn.textContent = '…';
    try {
      await api.auth.sendSignupOtp(email);
      document.getElementById('signup-otp-email').textContent = email;
      signupStep = 'code';
      updateSignupStepUI();
      document.getElementById('signup-otp').focus();
    } catch (err) {
      showAuthError(err);
      btn.textContent = btnTextBeforeSubmit;
    }
    return;
  }

  if (signupStep === 'code') {
    const otp = document.getElementById('signup-otp').value.trim();
    if (otp.length !== 6) {
      errEl.textContent = 'Enter the 6-digit code.';
      errEl.classList.add('show');
      return;
    }
    btn.textContent = '…';
    try {
      await api.auth.verifySignupOtp(email, otp);
      verifiedSignupEmail = email;
      signupStep = 'details';
      updateSignupStepUI();
      document.getElementById('auth-name').focus();
    } catch (err) {
      showAuthError(err);
      btn.textContent = btnTextBeforeSubmit;
    }
    return;
  }

  // signupStep === 'details'
  const password = document.getElementById('auth-password').value;
  const confirm  = document.getElementById('auth-confirm').value;
  const name     = document.getElementById('auth-name').value.trim() || email.split('@')[0];
  if (password !== confirm) {
    errEl.textContent = 'Passwords do not match.';
    errEl.classList.add('show');
    return;
  }
  if (password.length < 12) {
    errEl.textContent = 'Password must be at least 12 characters.';
    errEl.classList.add('show');
    return;
  }
  btn.textContent = '…';
  try {
    await api.auth.signUp(name, email, password);
    await afterAuth();
  } catch (err) {
    showAuthError(err);
    btn.textContent = btnTextBeforeSubmit;
    // The server-side gate (hooks.before) consumes the verification row the
    // moment it passes, even if sign-up then fails for some other reason
    // (see src/lib/auth.ts's comment) — reflect that here rather than
    // leaving the UI claiming a still-verified email that the server no
    // longer honors.
    signupStep = 'email';
    verifiedSignupEmail = null;
    updateSignupStepUI();
  }
});

// ── PASSWORD RESET (forgot + set-new) ─────────────────────────────────────

const authTabsEl        = document.querySelector('.auth-tabs');
const authFormEl        = document.getElementById('auth-form');
const resetRequestView  = document.getElementById('reset-request-view');
const resetSetView      = document.getElementById('reset-set-view');

// Toggles the auth card between normal sign-in, the "email me a link" view,
// and the "set a new password" view.
function showAuthMode(mode) { // 'login' | 'reset-request' | 'reset-set'
  authTabsEl.classList.toggle('hidden', mode !== 'login');
  authFormEl.classList.toggle('hidden', mode !== 'login');
  resetRequestView.classList.toggle('hidden', mode !== 'reset-request');
  resetSetView.classList.toggle('hidden', mode !== 'reset-set');
  document.getElementById('auth-error').classList.remove('show');
}

document.getElementById('forgot-link-a').addEventListener('click', (e) => {
  e.preventDefault();
  document.getElementById('reset-request-msg').classList.remove('show');
  // Carry over whatever email was already typed on the sign-in form.
  document.getElementById('reset-email').value = document.getElementById('auth-email').value.trim();
  showAuthMode('reset-request');
});

document.getElementById('reset-request-back').addEventListener('click', (e) => {
  e.preventDefault();
  showAuthMode('login');
});

resetRequestView.addEventListener('submit', async (e) => {
  e.preventDefault();
  const msgEl = document.getElementById('reset-request-msg');
  const label = document.getElementById('reset-request-btn-label');
  const email = document.getElementById('reset-email').value.trim();
  msgEl.classList.remove('show');
  if (!email) return;
  label.textContent = 'Sending…';
  try {
    await api.auth.requestPasswordReset(email);
  } catch (err) {
    // Better Auth returns success even for unknown emails (anti-enumeration);
    // only a transport error reaches here. Still show the neutral message.
    console.error('[reset] request error', err);
  }
  msgEl.style.color = 'var(--positive)';
  msgEl.textContent = 'If that email has an account, a reset link is on its way. Check your inbox.';
  msgEl.classList.add('show');
  label.textContent = 'Send reset link';
});

resetSetView.addEventListener('submit', async (e) => {
  e.preventDefault();
  const msgEl = document.getElementById('reset-set-msg');
  const label = document.getElementById('reset-set-btn-label');
  const pw      = document.getElementById('reset-new-password').value;
  const confirm = document.getElementById('reset-confirm-password').value;
  msgEl.classList.remove('show');
  msgEl.style.color = '';
  if (pw.length < 12)  { msgEl.textContent = 'Password must be at least 12 characters.'; msgEl.classList.add('show'); return; }
  if (pw !== confirm)  { msgEl.textContent = 'Passwords do not match.'; msgEl.classList.add('show'); return; }
  const token = new URLSearchParams(location.search).get('reset_token');
  if (!token) { msgEl.textContent = 'Reset link is invalid or expired. Request a new one.'; msgEl.classList.add('show'); return; }
  label.textContent = 'Saving…';
  try {
    await api.auth.resetPassword(pw, token);
    // Strip the token from the URL so a refresh doesn't re-open this view.
    history.replaceState(null, '', location.pathname);
    showAuthMode('login');
    const errEl = document.getElementById('auth-error');
    errEl.style.color = 'var(--positive)';
    errEl.textContent = 'Password updated. Sign in with your new password.';
    errEl.classList.add('show');
  } catch (err) {
    msgEl.textContent = err?.message || 'Reset link is invalid or expired. Request a new one.';
    msgEl.classList.add('show');
    label.textContent = 'Set new password';
  }
});

// ── POST-AUTH ROUTING ─────────────────────────────────────────────────────

async function afterAuth() {
  let status;
  try {
    status = await api.get('/api/onboarding/status');
  } catch (err) {
    console.error('[afterAuth] onboarding status failed:', err);
    throw new Error(`Session OK but dashboard failed to load (${err?.message ?? err?.status ?? 'network error'}). Try refreshing.`);
  }
  const { needsOnboarding, step } = status;
  if (needsOnboarding) {
    await startOnboarding(step + 1);
  } else {
    await showApp();
  }
}

// ── BIRTHDAY CHECK ────────────────────────────────────────────────────────

async function checkBirthday() {
  let userData;
  try { userData = await api.auth.session(); } catch { return; }
  if (!userData?.user) return;

  const today = new Date();
  const { birthMonth, birthDay, birthYear } = userData.user ?? {};

  if (!birthMonth || !birthDay) return;
  if (birthMonth !== today.getMonth() + 1 || birthDay !== today.getDate()) return;

  const seenKey = `bday-seen-${today.getFullYear()}`;
  if (localStorage.getItem(seenKey)) return;
  localStorage.setItem(seenKey, '1');

  const age = birthYear
    ? today.getFullYear() - birthYear - (
        today.getMonth() + 1 < birthMonth ||
        (today.getMonth() + 1 === birthMonth && today.getDate() < birthDay) ? 1 : 0
      )
    : null;

  let nwData;
  try { nwData = await api.networth.latest(); } catch {}

  const name = userData.user.name?.split(' ')[0] ?? 'You';

  document.getElementById('bday-name').textContent = `Happy Birthday, ${name}`;
  document.getElementById('bday-age').textContent  = age ? `You're ${age} today.` : 'Your special day.';

  const snapEl = document.getElementById('bday-snap');
  if (nwData) {
    const milestone = getNextMilestone(nwData.netWorth);
    snapEl.innerHTML = `
      <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:var(--space-3)">Your financial snapshot at ${age ?? 'this age'}</div>
      <div class="birthday-snap-row">
        <span>Net worth</span>
        <span class="birthday-snap-val" style="color:var(--positive)">${fmt$(nwData.netWorth)}</span>
      </div>
      <div class="birthday-snap-row">
        <span>Next milestone</span>
        <span class="birthday-snap-val">${milestone ? fmt$(milestone) : '—'}</span>
      </div>
      <div class="birthday-snap-row">
        <span>Roth IRA</span>
        <span class="birthday-snap-val">${fmt$(nwData.rothIra ?? 0)} / $7,000</span>
      </div>`;
  } else {
    snapEl.innerHTML = `<p style="color:var(--text-secondary);font-size:.875rem">Add a net worth snapshot to see your financial picture here each year.</p>`;
  }

  birthdayOverlay.classList.remove('hidden');

  document.getElementById('bday-dismiss-btn').addEventListener('click', () => {
    birthdayOverlay.classList.add('hidden');
  });
  document.getElementById('bday-snapshot-btn').addEventListener('click', () => {
    birthdayOverlay.classList.add('hidden');
    switchTab('progress');
    setTimeout(() => document.getElementById('add-snapshot-btn')?.click(), 600);
  });
}

function getNextMilestone(nw) {
  const milestones = [25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000];
  return milestones.find(m => m > nw) ?? null;
}

// ── ANNUAL LETTER ─────────────────────────────────────────────────────────
// Roadmap: "Annual letter (January 1st auto-generated summary)." Shows any
// time in January (not strictly the 1st — a user who doesn't open the app
// on exactly Jan 1 should still get it), once per year, same dismissal
// pattern as checkBirthday(). Summarizes the *previous* calendar year —
// net worth change, income/expenses logged, trading P&L, and any milestone
// thresholds crossed during that year — computed client-side from the same
// list endpoints Progress already uses, no new backend route.
async function checkAnnualLetter() {
  const today = new Date();
  if (today.getMonth() !== 0) return; // January only

  const year = today.getFullYear();
  const prevYear = year - 1;
  const seenKey = `annual-letter-seen-${year}`;
  if (localStorage.getItem(seenKey)) return;

  let snapshots, income, expenses, trades, userData;
  try {
    [snapshots, income, expenses, trades, userData] = await Promise.all([
      api.networth.list(),
      api.income.list(),
      api.expenses.list(),
      api.trades.list(),
      api.auth.session(),
    ]);
  } catch { return; }
  if (!userData?.user) return;

  // Nothing to summarize for a brand-new account with no prior-year data —
  // showing an empty letter would be worse than not showing one.
  const hadActivityLastYear =
    snapshots.some(s => s.date < `${year}-01-01`) ||
    income.some(i => i.payDate.startsWith(String(prevYear))) ||
    expenses.some(e => e.date.startsWith(String(prevYear)));
  if (!hadActivityLastYear) return;

  localStorage.setItem(seenKey, '1');

  const sorted = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));
  const netWorthStart = [...sorted].reverse().find(s => s.date < `${prevYear}-01-01`)?.netWorth ?? 0;
  const netWorthEnd   = [...sorted].reverse().find(s => s.date <= `${prevYear}-12-31`)?.netWorth ?? netWorthStart;
  const netWorthChange = netWorthEnd - netWorthStart;

  const yearIncome = income
    .filter(i => i.payDate.startsWith(String(prevYear)) && !i.isGross)
    .reduce((s, i) => s + i.amount, 0);
  const yearExpenses = expenses
    .filter(e => e.date.startsWith(String(prevYear)))
    .reduce((s, e) => s + e.amount, 0);
  const yearTradingPnl = trades
    .filter(t => (t.closedAt ?? '').startsWith(String(prevYear)))
    .reduce((s, t) => s + (t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0), 0);

  const ALL_MILESTONES = [25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000];
  const milestonesReached = ALL_MILESTONES.filter(m => netWorthStart < m && netWorthEnd >= m);

  const name = userData.user.name?.split(' ')[0] ?? 'You';
  document.getElementById('letter-title').textContent = `Your ${prevYear} in review`;
  document.getElementById('letter-subtitle').textContent = `${name}, here's where the year took you.`;
  document.getElementById('letter-snap').innerHTML = `
    <div class="birthday-snap-row">
      <span>Net worth change</span>
      <span class="birthday-snap-val" style="color:${netWorthChange >= 0 ? 'var(--positive)' : 'var(--negative)'}">${netWorthChange >= 0 ? '+' : ''}${fmt$(netWorthChange)}</span>
    </div>
    <div class="birthday-snap-row">
      <span>Income logged</span>
      <span class="birthday-snap-val" style="color:var(--positive)">${fmt$(yearIncome)}</span>
    </div>
    <div class="birthday-snap-row">
      <span>Expenses logged</span>
      <span class="birthday-snap-val" style="color:var(--negative)">${fmt$(yearExpenses)}</span>
    </div>
    <div class="birthday-snap-row">
      <span>Trading P&L</span>
      <span class="birthday-snap-val" style="color:${yearTradingPnl >= 0 ? 'var(--positive)' : 'var(--negative)'}">${yearTradingPnl >= 0 ? '+' : ''}${fmt$(yearTradingPnl)}</span>
    </div>
    ${milestonesReached.length > 0 ? `
    <div class="birthday-snap-row">
      <span>Milestones crossed</span>
      <span class="birthday-snap-val">${milestonesReached.map(m => fmt$(m, 0)).join(', ')}</span>
    </div>` : ''}`;
  document.getElementById('letter-quote').textContent =
    milestonesReached.length > 0
      ? `You crossed ${milestonesReached.length === 1 ? 'a milestone' : `${milestonesReached.length} milestones`} this year. On to the next one.`
      : 'Another year of real data. That compounds too.';

  annualLetterOverlay.classList.remove('hidden');
  document.getElementById('letter-dismiss-btn').addEventListener('click', () => {
    annualLetterOverlay.classList.add('hidden');
  });
}

// ── APP DISPLAY ───────────────────────────────────────────────────────────

async function showApp() {
  authScreen.classList.add('hidden');
  onboardingScreen.classList.add('hidden');
  appShell.classList.remove('hidden');
  fab.classList.remove('hidden');
  sidebarQuickAdd.classList.remove('hidden');
  switchTab('pulse');
  checkBirthday(); // non-blocking
  checkAnnualLetter(); // non-blocking
  updateCommissionCountdown(); // non-blocking
}

// ── SIDEBAR COMMISSION COUNTDOWN ──────────────────────────────────────────
// Persistent (desktop sidebar only — mobile has no equivalent slot), unlike
// the payday/commission banners that already exist on Pulse and Progress's
// Upcoming Flags card, both of which only show up while viewing those
// specific screens. Populated once at boot rather than per-tab-render.
async function updateCommissionCountdown() {
  const el = document.getElementById('sidebar-commission-countdown');
  if (!el) return;
  try {
    const thisYear = new Date().getFullYear();
    const [a, b] = await Promise.all([
      api.dashboard.paydays(thisYear),
      api.dashboard.paydays(thisYear + 1),
    ]);
    const todayStr = new Date().toISOString().split('T')[0];
    const commissionDays = [...a.commissionDays, ...b.commissionDays].filter(d => d >= todayStr).sort();
    if (commissionDays.length === 0) { el.innerHTML = ''; return; }
    const next = commissionDays[0];
    const daysUntil = Math.round((new Date(next + 'T12:00:00') - new Date(todayStr + 'T12:00:00')) / 86_400_000);
    el.innerHTML = `
      <div style="background:var(--bg-tertiary);border-radius:var(--radius-md);padding:var(--space-3)">
        <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Next commission</div>
        <div style="font-size:.85rem;font-weight:700;margin-top:4px">${daysUntil === 0 ? 'Today' : `${daysUntil} day${daysUntil === 1 ? '' : 's'}`}</div>
        <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:2px">${esc(next)}</div>
      </div>`;
  } catch {
    el.innerHTML = ''; // fails silently — this widget is a convenience, not critical path
  }
}

// ── ONBOARDING → APP HANDOFF ──────────────────────────────────────────────

window.addEventListener('onboarding-complete', () => showApp());

// ── TAB ROUTING ───────────────────────────────────────────────────────────

let activeTab = 'pulse';
const rendered = new Set();

const tabPanels = {
  pulse:    document.getElementById('panel-pulse'),
  plan:     document.getElementById('panel-plan'),
  progress: document.getElementById('panel-progress'),
  settings: document.getElementById('panel-settings'),
};

function switchTab(tab) {
  activeTab = tab;
  if (tab !== 'plan') hideDayDrawer(); // fixed-position drawer would otherwise stay visible over any other tab

  Object.entries(tabPanels).forEach(([key, el]) => {
    if (el) el.classList.toggle('active', key === tab);
  });
  document.querySelectorAll('.nav-item').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
  });
  document.querySelectorAll('.tab-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
  });

  // FAB hidden on progress + settings — and the desktop sidebar button tracks it
  // exactly, so quick capture is available on the same tabs at every width.
  const quickCaptureHidden = tab === 'progress' || tab === 'settings';
  fab.classList.toggle('hidden', quickCaptureHidden);
  sidebarQuickAdd.classList.toggle('hidden', quickCaptureHidden);

  // Always re-render on switch. Previously only Plan/Settings forced a
  // fresh render here; Pulse/Progress rendered once and relied entirely on
  // save handlers (`rendered.delete('progress'); renderTab('progress')`) to
  // refresh them afterwards. But those handlers delete from `rendered`
  // without ever re-adding it — nothing did, since this branch used to skip
  // re-adding for anything already in the Set — so the *first* save after
  // visiting Pulse/Progress refreshed correctly, and every save after that
  // silently no-opped (`rendered.has(tab)` was already false) until the
  // user navigated away and back. Confirmed live: closing a trade right
  // after creating it left the UI showing the stale "OPEN" trade with no
  // P&L until a full page reload. Re-rendering on every switch — like
  // Plan/Settings already did — makes every tab always reflect real data
  // the moment you look at it, and removes the need for the Set to gate
  // anything here at all.
  rendered.add(tab);
  renderTab(tab);
}

async function renderTab(tab) {
  const el = tabPanels[tab];
  if (!el) return;
  if      (tab === 'pulse')    await renderPulse(el);
  else if (tab === 'plan')     await renderPlan(el);
  else if (tab === 'progress') await renderProgress(el);
  else if (tab === 'settings') await renderSettings(el);
}

document.querySelectorAll('[data-tab]').forEach(btn => {
  btn.addEventListener('click', () => {
    const tab = btn.dataset.tab;
    if (!['pulse','plan','progress','settings'].includes(tab)) return;
    switchTab(tab);
  });
});

// ── FAB — QUICK CAPTURE ───────────────────────────────────────────────────

fab.addEventListener('click', () => openQuickCapture());
sidebarQuickAdd.addEventListener('click', () => openQuickCapture());

function openQuickCapture() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  content.innerHTML = `
    <div class="sheet-title">Quick capture</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3)">
      ${[
        { id: 'qc-task',       icon: '✅', label: 'Add task',          desc: 'Save to inbox' },
        { id: 'qc-block',      icon: '📅', label: 'Schedule block',    desc: 'Add to calendar' },
        { id: 'qc-income',     icon: '💵', label: 'Log paycheck',      desc: 'Income entry' },
        { id: 'qc-expense',    icon: '🧾', label: 'Log expense',       desc: 'Spending entry' },
        { id: 'qc-commission', icon: '💰', label: 'Log commission',    desc: 'Commission income' },
        { id: 'qc-trade',      icon: '📈', label: 'Log trade',         desc: 'New trade entry' },
        { id: 'qc-roth',       icon: '🏦', label: 'Roth IRA',          desc: 'Log contribution' },
        { id: 'qc-networth',   icon: '📊', label: 'Net worth snapshot',desc: 'Monthly check-in' },
      ].map(btn => `
        <button class="qc-btn" id="${btn.id}">
          <div style="font-size:1.5rem;margin-bottom:var(--space-1)">${btn.icon}</div>
          <div style="font-weight:700;font-size:.875rem">${btn.label}</div>
          <div style="font-size:.75rem;color:var(--text-tertiary)">${btn.desc}</div>
        </button>`).join('')}
    </div>
    <div style="text-align:center;margin-top:var(--space-3);font-size:.75rem;color:var(--text-tertiary)">Quick captures save instantly.</div>`;

  presentSheet();

  // Wire each button
  content.querySelector('#qc-task')?.addEventListener('click', () => {
    closeSheet();
    if (activeTab !== 'plan') {
      rendered.delete('plan');
      switchTab('plan');
      setTimeout(() => openAddTaskSheet(), 400);
    } else {
      openAddTaskSheet();
    }
  });

  content.querySelector('#qc-block')?.addEventListener('click', () => {
    closeSheet();
    if (activeTab !== 'plan') {
      rendered.delete('plan');
      switchTab('plan');
      setTimeout(() => openAddBlockSheet(), 400);
    } else {
      openAddBlockSheet();
    }
  });

  content.querySelector('#qc-income')?.addEventListener('click', () => {
    closeSheet();
    openIncomeSheet();
  });

  content.querySelector('#qc-expense')?.addEventListener('click', () => {
    closeSheet();
    openExpenseSheet();
  });

  content.querySelector('#qc-commission')?.addEventListener('click', () => {
    closeSheet();
    openCommissionSheet();
  });

  content.querySelector('#qc-trade')?.addEventListener('click', () => {
    closeSheet();
    if (activeTab !== 'progress') {
      switchTab('progress');
      setTimeout(() => openTradeSheet(), 500);
    } else {
      openTradeSheet();
    }
  });

  content.querySelector('#qc-roth')?.addEventListener('click', () => {
    closeSheet();
    openRothSheet();
  });

  content.querySelector('#qc-networth')?.addEventListener('click', () => {
    closeSheet();
    if (activeTab !== 'progress') {
      switchTab('progress');
      setTimeout(() => document.getElementById('add-snapshot-btn')?.click(), 500);
    } else {
      document.getElementById('add-snapshot-btn')?.click();
    }
  });
}

// ── QUICK CAPTURE FORMS ───────────────────────────────────────────────────

// These are global sheet openers — also exported so any tab can call them

export function openAddTaskSheet(prefill = {}) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  let cats = [];
  api.categories().then(c => {
    cats = c;
    const catSel = content.querySelector('#task-cat');
    if (catSel) {
      catSel.innerHTML = `<option value="">No category (Inbox)</option>${cats.map(c => `<option value="${esc(c.id)}">${esc(c.name)}</option>`).join('')}`;
      if (prefill.categoryId) catSel.value = prefill.categoryId;
    }
  }).catch(() => {});

  let sourceRows = (prefill.sources ?? []).map(s => ({ label: s.label, url: s.url }));

  content.innerHTML = `
    <div class="sheet-title">${prefill.id ? 'Edit task' : 'New task'}</div>
    <div class="form-group">
      <label class="form-label">Title *</label>
      <input type="text" id="task-title" value="${esc(prefill.title ?? '')}" placeholder="What needs to get done?" autofocus>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Category</label>
        <select id="task-cat"><option value="">Loading…</option></select>
      </div>
      <div class="form-group">
        <label class="form-label">Priority</label>
        <select id="task-priority">
          <option value="medium" ${(prefill.priority ?? 'medium') === 'medium' ? 'selected' : ''}>Medium</option>
          <option value="high"   ${prefill.priority === 'high'   ? 'selected' : ''}>High</option>
          <option value="low"    ${prefill.priority === 'low'    ? 'selected' : ''}>Low</option>
        </select>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Task type</label>
        <select id="task-type">
          <option value="one_time"  ${(prefill.taskType ?? 'one_time') === 'one_time' ? 'selected' : ''}>One-time</option>
          <option value="recurring" ${prefill.taskType === 'recurring' ? 'selected' : ''}>Recurring</option>
        </select>
      </div>
      <div class="form-group">
        <label class="form-label">Est. hours</label>
        <input type="number" id="task-hours" value="${prefill.estimatedHours ?? ''}" placeholder="0" step="0.5" min="0">
      </div>
    </div>
    <div class="form-group" id="task-recurrence-wrap" style="display:${prefill.taskType === 'recurring' ? 'block' : 'none'}">
      <label class="form-label">Repeats every</label>
      <select id="task-recurrence-interval">
        <option value="1"  ${prefill.recurrenceIntervalDays === 1  ? 'selected' : ''}>Day</option>
        <option value="7"  ${!prefill.recurrenceIntervalDays || prefill.recurrenceIntervalDays === 7 ? 'selected' : ''}>Week</option>
        <option value="14" ${prefill.recurrenceIntervalDays === 14 ? 'selected' : ''}>2 weeks</option>
        <option value="30" ${prefill.recurrenceIntervalDays === 30 ? 'selected' : ''}>Month</option>
      </select>
      <div class="field-helper">Completing this task automatically creates the next one, due date advanced by this interval. Requires a due date below.</div>
    </div>
    <div class="form-group">
      <label class="form-label">Deadline</label>
      <div style="display:flex;gap:var(--space-2)">
        <select id="task-deadline-type" style="width:140px;flex-shrink:0">
          <option value="none" ${(prefill.deadlineType ?? 'none') === 'none' ? 'selected' : ''}>None</option>
          <option value="soft" ${prefill.deadlineType === 'soft' ? 'selected' : ''}>Soft target</option>
          <option value="hard" ${prefill.deadlineType === 'hard' ? 'selected' : ''}>Hard deadline</option>
        </select>
        <input type="date" id="task-due" value="${prefill.dueDate ?? ''}" style="flex:1;display:${(prefill.deadlineType && prefill.deadlineType !== 'none') || prefill.taskType === 'recurring' ? 'block' : 'none'}">
      </div>
    </div>
    <div class="form-group">
      <label style="display:flex;align-items:center;gap:var(--space-2);cursor:pointer;font-size:.875rem">
        <input type="checkbox" id="task-can-split" ${(prefill.canSplit ?? true) ? 'checked' : ''}> Can be split across multiple sessions
      </label>
    </div>
    <div class="form-group" id="task-min-session-wrap" style="display:${(prefill.canSplit ?? true) ? 'block' : 'none'}">
      <label class="form-label">Minimum session length (hours)</label>
      <input type="number" id="task-min-session" value="${prefill.minSessionHours ?? ''}" placeholder="e.g. 1.5" step="0.5" min="0">
    </div>
    <div class="form-group">
      <label class="form-label">Preferred time <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <select id="task-time-pref">
        <option value=""         ${!prefill.preferredTimeOfDay ? 'selected' : ''}>No preference</option>
        <option value="morning"   ${prefill.preferredTimeOfDay === 'morning'   ? 'selected' : ''}>Morning (early in your day)</option>
        <option value="afternoon" ${prefill.preferredTimeOfDay === 'afternoon' ? 'selected' : ''}>Afternoon (middle of your day)</option>
        <option value="night"     ${prefill.preferredTimeOfDay === 'night'     ? 'selected' : ''}>Night (late in your day)</option>
        <option value="custom"    ${prefill.preferredTimeOfDay === 'custom'    ? 'selected' : ''}>Exact time range…</option>
      </select>
      <div class="field-helper" style="margin-top:4px">Morning/afternoon/night are relative to your own Sleep Window in Settings, not the clock — this still works for night shifts.</div>
    </div>
    <div class="form-row" id="task-time-pref-custom-wrap" style="display:${prefill.preferredTimeOfDay === 'custom' ? 'flex' : 'none'}">
      <div class="form-group">
        <label class="form-label">From</label>
        <input type="time" id="task-time-pref-start" value="${prefill.preferredStartTime ?? ''}">
      </div>
      <div class="form-group">
        <label class="form-label">To</label>
        <input type="time" id="task-time-pref-end" value="${prefill.preferredEndTime ?? ''}">
      </div>
    </div>
    <div class="form-group">
      <label class="form-label">Description</label>
      <textarea id="task-desc" rows="2" placeholder="What needs to happen?" style="resize:vertical">${esc(prefill.description ?? '')}</textarea>
    </div>
    <div class="form-group">
      <label class="form-label">Notes <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <textarea id="task-notes" rows="2" placeholder="Any other context, links, or reminders…" style="resize:vertical">${esc(prefill.notes ?? '')}</textarea>
    </div>
    <div class="form-group">
      <label class="form-label">Source links</label>
      <div id="task-sources-list"></div>
      <button type="button" class="btn btn-ghost" id="task-add-source" style="font-size:.8rem;padding:6px 10px;margin-top:var(--space-1)">+ Add link</button>
    </div>
    <div id="task-err" class="auth-error"></div>
    <div style="display:flex;gap:var(--space-3)">
      <button class="btn btn-primary w-full" id="save-task-btn" style="justify-content:center;padding:12px">${prefill.id ? 'Save changes' : 'Add task'}</button>
      ${prefill.id ? `<button class="btn btn-danger" id="delete-task-btn" style="padding:12px 16px">Delete</button>` : ''}
    </div>`;

  // Deadline type toggle — a recurring task keeps its due date visible
  // regardless of deadlineType, since recurrence needs it as the base to
  // advance from.
  content.querySelector('#task-deadline-type').addEventListener('change', (e) => {
    const dateInput = content.querySelector('#task-due');
    const isRecurring = content.querySelector('#task-type').value === 'recurring';
    dateInput.style.display = (e.target.value !== 'none' || isRecurring) ? 'block' : 'none';
    if (e.target.value === 'none' && !isRecurring) dateInput.value = '';
  });

  // Task type toggle — recurring reveals the frequency selector and forces
  // the due date field visible (it's the base recurrence advances from).
  content.querySelector('#task-type').addEventListener('change', (e) => {
    const isRecurring = e.target.value === 'recurring';
    content.querySelector('#task-recurrence-wrap').style.display = isRecurring ? 'block' : 'none';
    if (isRecurring) content.querySelector('#task-due').style.display = 'block';
  });

  // Can-split toggle
  content.querySelector('#task-can-split').addEventListener('change', (e) => {
    content.querySelector('#task-min-session-wrap').style.display = e.target.checked ? 'block' : 'none';
  });

  // Preferred-time toggle — only 'custom' needs the exact from/to inputs
  content.querySelector('#task-time-pref').addEventListener('change', (e) => {
    content.querySelector('#task-time-pref-custom-wrap').style.display = e.target.value === 'custom' ? 'flex' : 'none';
  });

  // Source links — dynamic add/remove rows
  function renderSourceRows() {
    const list = content.querySelector('#task-sources-list');
    list.innerHTML = sourceRows.map((row, i) => `
      <div class="form-row task-source-row" data-idx="${i}" style="margin-bottom:var(--space-2);align-items:flex-start">
        <input type="text" class="task-source-label" data-idx="${i}" value="${esc(row.label)}" placeholder="Label" style="flex:1">
        <input type="url" class="task-source-url" data-idx="${i}" value="${esc(row.url)}" placeholder="https://…" style="flex:2">
        <button type="button" class="btn btn-ghost task-source-remove" data-idx="${i}" style="padding:8px;flex-shrink:0">✕</button>
      </div>`).join('');

    list.querySelectorAll('.task-source-label').forEach(input => {
      input.addEventListener('input', () => { sourceRows[+input.dataset.idx].label = input.value; });
    });
    list.querySelectorAll('.task-source-url').forEach(input => {
      input.addEventListener('input', () => { sourceRows[+input.dataset.idx].url = input.value; });
    });
    list.querySelectorAll('.task-source-remove').forEach(btn => {
      btn.addEventListener('click', () => { sourceRows.splice(+btn.dataset.idx, 1); renderSourceRows(); });
    });
  }
  renderSourceRows();

  content.querySelector('#task-add-source').addEventListener('click', () => {
    sourceRows.push({ label: '', url: '' });
    renderSourceRows();
  });

  content.querySelector('#save-task-btn').addEventListener('click', async () => {
    const errEl = content.querySelector('#task-err');
    errEl.classList.remove('show');
    const title        = content.querySelector('#task-title').value.trim();
    const categoryId   = content.querySelector('#task-cat').value || null;
    const priority     = content.querySelector('#task-priority').value;
    const taskType     = content.querySelector('#task-type').value;
    const estimatedHours = parseFloat(content.querySelector('#task-hours').value) || null;
    const deadlineType = content.querySelector('#task-deadline-type').value;
    const dueDate      = content.querySelector('#task-due').value || null;
    const recurrenceIntervalDays = taskType === 'recurring' ? parseInt(content.querySelector('#task-recurrence-interval').value) : null;
    const canSplit      = content.querySelector('#task-can-split').checked;
    const minSessionHours = canSplit ? (parseFloat(content.querySelector('#task-min-session').value) || null) : null;
    const preferredTimeOfDay = content.querySelector('#task-time-pref').value || null;
    const preferredStartTime = preferredTimeOfDay === 'custom' ? (content.querySelector('#task-time-pref-start').value || null) : null;
    const preferredEndTime   = preferredTimeOfDay === 'custom' ? (content.querySelector('#task-time-pref-end').value || null) : null;
    const description  = content.querySelector('#task-desc').value.trim() || null;
    const notes        = content.querySelector('#task-notes').value.trim() || null;
    const sources = sourceRows
      .map(r => ({ label: r.label.trim(), url: r.url.trim() }))
      .filter(r => r.label && r.url);

    if (!title) { errEl.textContent = 'Title is required'; errEl.classList.add('show'); return; }
    if (sourceRows.some(r => (r.label.trim() || r.url.trim()) && !(r.label.trim() && r.url.trim()))) {
      errEl.textContent = 'Each source link needs both a label and a URL'; errEl.classList.add('show'); return;
    }
    if (taskType === 'recurring' && !dueDate) {
      errEl.textContent = 'Recurring tasks need a due date to advance from'; errEl.classList.add('show'); return;
    }
    if (preferredTimeOfDay === 'custom' && (!preferredStartTime || !preferredEndTime)) {
      errEl.textContent = 'Exact time range needs both a start and end time'; errEl.classList.add('show'); return;
    }

    const body = { title, categoryId, priority, taskType, recurrenceIntervalDays, estimatedHours, deadlineType, dueDate, canSplit, minSessionHours, preferredTimeOfDay, preferredStartTime, preferredEndTime, description, notes, sources };
    try {
      if (prefill.id) {
        await api.tasks.update(prefill.id, body);
      } else {
        await api.tasks.create({ ...body, status: 'open' });
      }
      closeSheet();
      showToast(prefill.id ? 'Task updated' : 'Task added');
      if (rendered.has('pulse')) { renderTab('pulse'); }
      if (rendered.has('plan'))  { renderTab('plan');  }
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to save';
      errEl.classList.add('show');
    }
  });

  content.querySelector('#delete-task-btn')?.addEventListener('click', async () => {
    if (!confirm('Delete this task?')) return;
    await api.tasks.delete(prefill.id).catch(() => {});
    closeSheet();
    showToast('Task deleted');
    rendered.delete('plan'); renderTab('plan');
  });

  presentSheet();
  content.querySelector('#task-title')?.focus();
}

const DOW = [
  { code: 'sun', label: 'S' }, { code: 'mon', label: 'M' }, { code: 'tue', label: 'T' },
  { code: 'wed', label: 'W' }, { code: 'thu', label: 'T' }, { code: 'fri', label: 'F' },
  { code: 'sat', label: 'S' },
];

export function openAddBlockSheet(prefill = {}) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const today   = new Date().toISOString().split('T')[0];

  const isRuleInstance = !!prefill.ruleId;      // editing an occurrence of a recurring series
  const isNew          = !prefill.id && !prefill.ruleId;
  const startDow       = DOW[new Date((prefill.date ?? today) + 'T00:00:00').getUTCDay()].code;
  const startDom       = Number((prefill.date ?? today).split('-')[2]);

  content.innerHTML = `
    <div class="sheet-title">${isRuleInstance ? 'Edit recurring block' : prefill.id ? 'Edit block' : 'New block'}</div>

    ${isRuleInstance ? `
    <div class="form-group" id="block-scope-wrap">
      <label class="form-label">Applies to</label>
      <div style="display:flex;gap:16px;font-size:.875rem">
        <label style="display:flex;align-items:center;gap:6px"><input type="radio" name="block-scope" value="one" checked> This event only</label>
        <label style="display:flex;align-items:center;gap:6px"><input type="radio" name="block-scope" value="all"> All events in series</label>
      </div>
    </div>
    ` : ''}

    <div class="form-group">
      <label class="form-label">Label *</label>
      <input type="text" id="block-label" value="${esc(prefill.title ?? prefill.label ?? '')}" placeholder="What are you working on?" autofocus>
    </div>
    <div class="form-row">
      <div class="form-group" id="block-date-wrap">
        <label class="form-label">Date *</label>
        <input type="date" id="block-date" value="${prefill.date ?? today}" ${isRuleInstance ? 'disabled' : ''}>
        ${isRuleInstance ? `<div style="font-size:.7rem;color:var(--text-tertiary);margin-top:2px">Rescheduling a single instance isn't supported yet — cancel this one and add a new block instead.</div>` : ''}
      </div>
      <div class="form-group">
        <label class="form-label">Category</label>
        <select id="block-cat"><option value="">Loading…</option></select>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Start time</label>
        <input type="time" id="block-start" value="${prefill.startTime ?? ''}">
      </div>
      <div class="form-group">
        <label class="form-label">End time</label>
        <input type="time" id="block-end" value="${prefill.endTime ?? ''}">
      </div>
    </div>
    <div id="block-overlap-warning" class="auth-error" style="background:rgba(240,160,48,.12);color:var(--caution);border-color:rgba(240,160,48,.3)"></div>

    <div class="form-group" style="position:relative">
      <label class="form-label">Link to task (optional)</label>
      <input type="text" id="block-task-search" placeholder="Search tasks…" autocomplete="off">
      <input type="hidden" id="block-task" value="${esc(prefill.taskId ?? '')}">
      <div id="block-task-dropdown" style="display:none;position:absolute;left:0;right:0;top:100%;z-index:20;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);max-height:200px;overflow-y:auto;box-shadow:0 4px 16px rgba(0,0,0,.3)"></div>
      <div class="field-helper">Linking a task here is what makes it show "✓ Scheduled" in the Tasks list.</div>
    </div>
    <div class="form-group">
      <label class="form-label">Location <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <input type="text" id="block-location" value="${esc(prefill.location ?? '')}" placeholder="e.g. Room 204, Zoom, home office">
    </div>

    ${isNew ? `
    <div class="form-group" style="margin-top:var(--space-1)">
      <label class="form-label">Repeat</label>
      <select id="block-freq">
        <option value="none">Does not repeat</option>
        <option value="daily">Every day</option>
        <option value="weekly">On specific days of the week</option>
        <option value="monthly">On a day of the month</option>
      </select>
    </div>
    <div class="form-group" id="block-dow-wrap" style="display:none">
      <label class="form-label">Which days</label>
      <div style="display:flex;gap:6px">
        ${DOW.map(d => `<button type="button" class="btn btn-ghost dow-pill" data-dow="${d.code}" style="padding:8px 10px;min-width:36px">${d.label}</button>`).join('')}
      </div>
    </div>
    <div class="form-group" id="block-dom-wrap" style="display:none">
      <label class="form-label">Day of month</label>
      <input type="number" id="block-dom" min="1" max="31" value="${startDom}" style="width:100px">
    </div>
    <div class="form-group" id="block-freq-until-wrap" style="display:none">
      <label class="form-label">Repeat until (optional)</label>
      <input type="date" id="block-freq-until" style="width:100%">
      <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:2px">Leave blank to repeat indefinitely.</div>
    </div>
    <div id="block-freq-preview" style="font-size:.75rem;color:var(--text-tertiary);margin-bottom:var(--space-3)"></div>
    ` : ''}

    <div class="form-group">
      <label class="form-label">Notes (optional)</label>
      <textarea id="block-notes" rows="2" style="resize:vertical" placeholder="…">${esc(prefill.notes ?? '')}</textarea>
    </div>
    <div id="block-err" class="auth-error"></div>
    <div style="display:flex;gap:var(--space-3)">
      <button class="btn btn-primary w-full" id="save-block-btn" style="justify-content:center;padding:12px">${prefill.id || isRuleInstance ? 'Save changes' : 'Add block'}</button>
      ${prefill.id || isRuleInstance ? `<button class="btn btn-danger" id="delete-block-btn" style="padding:12px 16px">Delete</button>` : ''}
    </div>`;

  // Populate categories
  api.categories().then(c => {
    const sel = content.querySelector('#block-cat');
    if (sel) {
      sel.innerHTML = `<option value="">No category</option>${c.map(cat => `<option value="${esc(cat.id)}" style="color:${esc(cat.color)}">${esc(cat.name)}</option>`).join('')}`;
      if (prefill.categoryId) sel.value = prefill.categoryId;
    }
  }).catch(() => {});

  // Task-link search/autocomplete — unscheduled tasks first (spec: "searchable,
  // unscheduled first"). A previous pass here used a plain <select> instead,
  // reasoning there was no autocomplete component in this codebase to reuse;
  // built a minimal one scoped to just this field rather than leaving the gap.
  const taskSearchInput = content.querySelector('#block-task-search');
  const taskHiddenInput = content.querySelector('#block-task');
  const taskDropdown    = content.querySelector('#block-task-dropdown');
  let linkableTasks = [];

  function renderTaskDropdown(filterText) {
    const q = filterText.trim().toLowerCase();
    const matches = (q ? linkableTasks.filter(t => t.title.toLowerCase().includes(q)) : linkableTasks).slice(0, 8);
    if (matches.length === 0) {
      taskDropdown.innerHTML = `<div style="padding:8px 10px;font-size:.8rem;color:var(--text-tertiary)">No matching tasks</div>`;
    } else {
      taskDropdown.innerHTML = matches.map(t => `
        <div class="block-task-option" data-id="${esc(t.id)}" data-title="${esc(t.title)}" style="padding:8px 10px;font-size:.85rem;cursor:pointer;border-bottom:1px solid var(--bg-tertiary)">
          ${esc(t.title)}${t.scheduled ? ` <span style="color:var(--text-tertiary);font-size:.75rem">(already scheduled)</span>` : ''}
        </div>`).join('');
    }
    taskDropdown.style.display = 'block';
  }

  Promise.all([api.tasks.list({ status: 'open' }), api.blocks.list()]).then(([tasksList, allBlocks]) => {
    const scheduledIds = new Set(allBlocks.map(b => b.taskId).filter(Boolean));
    linkableTasks = [...tasksList]
      .map(t => ({ id: t.id, title: t.title, scheduled: scheduledIds.has(t.id) }))
      .sort((a, b) => (a.scheduled ? 1 : 0) - (b.scheduled ? 1 : 0) || a.title.localeCompare(b.title));
    if (prefill.taskId) {
      const current = linkableTasks.find(t => t.id === prefill.taskId);
      if (current) taskSearchInput.value = current.title;
    }
  }).catch(() => {});

  taskSearchInput.addEventListener('focus', () => renderTaskDropdown(taskSearchInput.value));
  taskSearchInput.addEventListener('input', () => {
    taskHiddenInput.value = ''; // typing invalidates whatever was selected until a result is clicked
    renderTaskDropdown(taskSearchInput.value);
  });
  taskDropdown.addEventListener('mousedown', (e) => {
    // mousedown, not click — fires before the input's blur hides the dropdown
    const opt = e.target.closest('.block-task-option');
    if (!opt) return;
    taskHiddenInput.value = opt.dataset.id;
    taskSearchInput.value = opt.dataset.title;
    taskDropdown.style.display = 'none';
  });
  document.addEventListener('click', (e) => {
    if (!taskSearchInput.contains(e.target) && !taskDropdown.contains(e.target)) {
      taskDropdown.style.display = 'none';
    }
  });

  // ── Overlap warning ──────────────────────────────────────────────────────
  // Inline, non-blocking (spec: "conflicts school") — checks the currently
  // entered date/start/end against every other block AND school class on that
  // date, not just school. A conflict never prevents saving; it's a heads-up.
  const overlapEl = content.querySelector('#block-overlap-warning');
  function timeToMin(t) { const [h, m] = t.split(':').map(Number); return h * 60 + m; }
  function rangesOverlap(aStart, aEnd, bStart, bEnd) {
    return timeToMin(aStart) < timeToMin(bEnd) && timeToMin(bStart) < timeToMin(aEnd);
  }
  async function checkOverlap() {
    const date  = content.querySelector('#block-date')?.value;
    const start = content.querySelector('#block-start')?.value;
    const end   = content.querySelector('#block-end')?.value;
    if (!overlapEl || !date || !start || !end) { overlapEl?.classList.remove('show'); return; }

    const [allBlocks, schoolOnDate] = await Promise.all([
      api.blocks.list().catch(() => []),
      api.blocks.schoolBlocks({ from: date, to: date }).catch(() => []),
    ]);
    const others = [
      ...allBlocks
        .filter(b => b.date === date && b.id !== prefill.id && b.startTime && b.endTime)
        .map(b => ({ label: b.label ?? b.title, startTime: b.startTime, endTime: b.endTime })),
      ...schoolOnDate
        .filter(b => b.startTime && b.endTime)
        .map(b => ({ label: `${b.label ?? b.title} (class)`, startTime: b.startTime, endTime: b.endTime })),
    ];
    const conflicts = others.filter(o => rangesOverlap(start, end, o.startTime, o.endTime));

    if (conflicts.length) {
      // .textContent, not innerHTML — no esc() needed, the browser never parses this as HTML.
      overlapEl.textContent = `⚠ Overlaps with ${conflicts.map(c => c.label).join(', ')}`;
      overlapEl.classList.add('show');
    } else {
      overlapEl.classList.remove('show');
    }
  }
  ['block-date', 'block-start', 'block-end'].forEach(id => {
    content.querySelector(`#${id}`)?.addEventListener('change', checkOverlap);
  });
  checkOverlap();

  // ── Repeat picker (new blocks only) ─────────────────────────────────────
  const freqSel      = content.querySelector('#block-freq');
  const dowWrap       = content.querySelector('#block-dow-wrap');
  const domWrap       = content.querySelector('#block-dom-wrap');
  const freqUntilWrap = content.querySelector('#block-freq-until-wrap');
  const freqPreviewEl = content.querySelector('#block-freq-preview');
  const selectedDow   = new Set(freqSel ? [startDow] : []);

  function updateFreqPreview() {
    if (!freqSel || !freqPreviewEl) return;
    const mode = freqSel.value;
    if (mode === 'none')    { freqPreviewEl.textContent = ''; return; }
    if (mode === 'daily')   { freqPreviewEl.textContent = 'Repeats every day.'; return; }
    if (mode === 'weekly') {
      const names = DOW.filter(d => selectedDow.has(d.code)).map(d => d.code[0].toUpperCase() + d.code.slice(1));
      freqPreviewEl.textContent = names.length ? `Repeats every ${names.join(', ')}.` : 'Pick at least one day.';
      return;
    }
    if (mode === 'monthly') {
      const dom = content.querySelector('#block-dom')?.value || startDom;
      freqPreviewEl.textContent = `Repeats on day ${dom} of every month.`;
    }
  }

  freqSel?.addEventListener('change', () => {
    const mode = freqSel.value;
    if (dowWrap) dowWrap.style.display = mode === 'weekly'  ? 'block' : 'none';
    if (domWrap) domWrap.style.display = mode === 'monthly' ? 'block' : 'none';
    if (freqUntilWrap) freqUntilWrap.style.display = mode === 'none' ? 'none' : 'block';
    updateFreqPreview();
  });
  content.querySelectorAll('.dow-pill').forEach(btn => {
    if (selectedDow.has(btn.dataset.dow)) btn.classList.add('btn-primary');
    btn.addEventListener('click', () => {
      if (selectedDow.has(btn.dataset.dow)) { selectedDow.delete(btn.dataset.dow); btn.classList.remove('btn-primary'); }
      else                                  { selectedDow.add(btn.dataset.dow);    btn.classList.add('btn-primary'); }
      updateFreqPreview();
    });
  });
  content.querySelector('#block-dom')?.addEventListener('input', updateFreqPreview);

  function scope() {
    return content.querySelector('input[name="block-scope"]:checked')?.value ?? 'one';
  }

  content.querySelector('#save-block-btn').addEventListener('click', async () => {
    const errEl      = content.querySelector('#block-err');
    errEl.classList.remove('show');
    const title      = content.querySelector('#block-label').value.trim();
    const date       = content.querySelector('#block-date').value;
    const categoryId = content.querySelector('#block-cat').value || null;
    const startTime  = content.querySelector('#block-start').value || null;
    const endTime    = content.querySelector('#block-end').value || null;
    const taskId     = content.querySelector('#block-task').value || null;
    const notes      = content.querySelector('#block-notes').value.trim() || null;
    const location   = content.querySelector('#block-location').value.trim() || null;

    if (!title) { errEl.textContent = 'Label is required'; errEl.classList.add('show'); return; }
    if (!date)  { errEl.textContent = 'Date is required';  errEl.classList.add('show'); return; }

    try {
      if (isRuleInstance) {
        if (scope() === 'all') {
          await api.blocks.rules.update(prefill.ruleId, { title, categoryId, startTime, endTime, taskId, notes, location });
        } else {
          await api.blocks.rules.overrideInstance(prefill.ruleId, { date, title, categoryId, startTime, endTime, taskId, notes, location });
        }
      } else if (prefill.id) {
        await api.blocks.update(prefill.id, { title, date, categoryId, startTime, endTime, taskId, notes, location });
      } else {
        const freqMode = freqSel?.value ?? 'none';
        if (freqMode === 'none') {
          await api.blocks.create({ title, date, categoryId, startTime, endTime, taskId, notes, location });
        } else {
          if (freqMode === 'weekly' && selectedDow.size === 0) {
            errEl.textContent = 'Pick at least one day of the week'; errEl.classList.add('show'); return;
          }
          await api.blocks.rules.create({
            title, categoryId, startTime, endTime, taskId, notes, location,
            freq: freqMode,
            daysOfWeek: freqMode === 'weekly' ? [...selectedDow].join(',') : undefined,
            dayOfMonth: freqMode === 'monthly' ? Number(content.querySelector('#block-dom').value) : undefined,
            startDate:  date,
            endDate:    content.querySelector('#block-freq-until')?.value || undefined,
          });
        }
      }
      closeSheet();
      showToast(prefill.id || isRuleInstance ? 'Block updated' : 'Block scheduled');
      rendered.delete('pulse'); renderTab('pulse');
      rendered.delete('plan');  renderTab('plan');
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to save';
      errEl.classList.add('show');
      const saveBtn = content.querySelector('#save-block-btn');
      if (saveBtn) { saveBtn.disabled = false; }
    }
  });

  content.querySelector('#delete-block-btn')?.addEventListener('click', async () => {
    if (isRuleInstance) {
      if (scope() === 'all') {
        if (!confirm('Delete this entire recurring series? All future and past instances will be removed.')) return;
        await api.blocks.rules.delete(prefill.ruleId).catch(() => {});
      } else {
        if (!confirm('Delete just this occurrence? The rest of the series stays.')) return;
        await api.blocks.rules.cancelInstance(prefill.ruleId, prefill.date).catch(() => {});
      }
    } else {
      if (!confirm('Delete this block?')) return;
      await api.blocks.delete(prefill.id).catch(() => {});
    }
    closeSheet();
    showToast('Block deleted');
    rendered.delete('pulse'); renderTab('pulse');
    rendered.delete('plan');  renderTab('plan');
  });

  presentSheet();
}


export function openIncomeSheet(prefill = {}) {
  _openFinanceSheet('income', prefill);
}

export function openExpenseSheet(prefill = {}) {
  _openFinanceSheet('expense', prefill);
}

export function openCommissionSheet() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const now = new Date();
  const forMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}`;

  content.innerHTML = `
    <div class="sheet-title">Log commission</div>
    <div class="form-group">
      <label class="form-label">For month</label>
      <input type="month" id="comm-month" value="${forMonth}">
    </div>
    <div class="form-group">
      <label class="form-label">Status</label>
      <select id="comm-status">
        <option value="confirmed">Confirmed — I got it</option>
        <option value="expected">Expected — payday coming</option>
        <option value="confirmed_none">Not this month — confirmed none</option>
      </select>
    </div>
    <div class="form-group" id="comm-amount-group">
      <label class="form-label">Amount (net)</label>
      <input type="number" id="comm-amount" placeholder="0.00" step="0.01" style="font-family:var(--font-mono)">
    </div>
    <div class="form-group">
      <label class="form-label">Notes</label>
      <input type="text" id="comm-notes" placeholder="Optional">
    </div>
    <div id="comm-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="save-comm-btn" style="justify-content:center;padding:12px;margin-top:var(--space-2)">Save commission</button>`;

  content.querySelector('#comm-status').addEventListener('change', (e) => {
    content.querySelector('#comm-amount-group').style.display =
      e.target.value === 'confirmed_none' ? 'none' : 'block';
  });

  content.querySelector('#save-comm-btn').addEventListener('click', async () => {
    const errEl = content.querySelector('#comm-err');
    errEl.classList.remove('show');
    const forMonth = content.querySelector('#comm-month').value;
    const status   = content.querySelector('#comm-status').value;
    const amount   = parseFloat(content.querySelector('#comm-amount').value) || undefined;
    const notes    = content.querySelector('#comm-notes').value.trim() || undefined;

    if (!forMonth) { errEl.textContent = 'Month is required'; errEl.classList.add('show'); return; }
    try {
      await api.income.commission.create({ forMonth, status, amount, receivedDate: new Date().toISOString().split('T')[0], notes });
      closeSheet();
      showToast('Commission logged');
      if (rendered.has('progress')) { renderTab('progress'); }
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed';
      errEl.classList.add('show');
    }
  });

  presentSheet();
}

export function openTradeSheet(prefill = {}) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const today   = new Date().toISOString().split('T')[0];

  let entryFills = (prefill.fills ?? []).filter(f => f.side === 'buy')
    .map(f => ({ price: f.price, quantity: f.quantity, filledAt: f.filledAt }));
  let exitFills = (prefill.fills ?? []).filter(f => f.side === 'sell')
    .map(f => ({ price: f.price, quantity: f.quantity, filledAt: f.filledAt }));
  if (!prefill.id && entryFills.length === 0) entryFills.push({ price: '', quantity: 1, filledAt: today });

  content.innerHTML = `
    <div class="sheet-title">${prefill.id ? 'Edit trade' : 'New trade'}</div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Ticker *</label>
        <input type="text" id="trade-ticker" value="${esc(prefill.ticker ?? '')}" placeholder="QQQ" style="text-transform:uppercase">
      </div>
      <div class="form-group">
        <label class="form-label">Type</label>
        <select id="trade-type">
          <option value="option" ${(prefill.tradeType ?? 'option') === 'option' ? 'selected' : ''}>Option</option>
          <option value="stock"  ${prefill.tradeType === 'stock'  ? 'selected' : ''}>Stock</option>
        </select>
      </div>
    </div>
    <div id="option-fields" style="${(prefill.tradeType ?? 'option') === 'stock' ? 'display:none' : ''}">
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Option type</label>
          <select id="trade-opt-type">
            <option value="call" ${(prefill.optionType ?? 'call') === 'call' ? 'selected' : ''}>Call</option>
            <option value="put"  ${prefill.optionType === 'put' ? 'selected' : ''}>Put</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Strike ($)</label>
          <input type="number" id="trade-strike" value="${prefill.strikePrice ?? ''}" step="0.5" placeholder="700.00" style="font-family:var(--font-mono)">
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Expiry date</label>
        <input type="date" id="trade-expiry" value="${prefill.expiryDate ?? ''}">
      </div>
    </div>
    <div class="form-group">
      <label class="form-label">Status</label>
      <select id="trade-status">
        <option value="open"              ${(prefill.status ?? 'open') === 'open'              ? 'selected' : ''}>Open</option>
        <option value="closed"            ${prefill.status === 'closed'                         ? 'selected' : ''}>Closed</option>
        <option value="expired_worthless" ${prefill.status === 'expired_worthless'              ? 'selected' : ''}>Expired worthless</option>
      </select>
    </div>
    <div class="form-group">
      <label class="form-label">Opened</label>
      <input type="date" id="trade-opened" value="${prefill.openedAt ?? today}">
    </div>

    <div class="form-group">
      <label class="form-label">Entry fills *</label>
      <div id="trade-entry-fills"></div>
      <button type="button" class="btn btn-ghost" id="trade-add-entry" style="font-size:.8rem;padding:6px 10px;margin-top:var(--space-1)">+ Add entry fill</button>
    </div>

    <div id="close-fields" style="${(prefill.status ?? 'open') === 'open' ? 'display:none' : ''}">
      <div class="form-group">
        <label class="form-label">Exit fills</label>
        <div id="trade-exit-fills"></div>
        <button type="button" class="btn btn-ghost" id="trade-add-exit" style="font-size:.8rem;padding:6px 10px;margin-top:var(--space-1)">+ Add exit fill</button>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Closed Date</label>
          <input type="date" id="trade-closed-date" value="${prefill.closedAt ?? today}">
        </div>
        <div class="form-group">
          <label class="form-label">Thesis Accuracy (1–10)</label>
          <input type="number" id="trade-accuracy" value="${prefill.thesisAccuracy ?? ''}" min="1" max="10" placeholder="Rating">
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Post-trade review</label>
        <textarea id="trade-review" rows="2" style="resize:vertical" placeholder="How did it go?">${esc(prefill.postTradeReview ?? '')}</textarea>
      </div>
    </div>

    <div class="card" id="trade-pnl-preview" style="margin:var(--space-2) 0"></div>

    <div class="form-group">
      <label class="form-label">Pre-trade thesis</label>
      <textarea id="trade-thesis" rows="2" style="resize:vertical" placeholder="Why are you making this trade?">${esc(prefill.preTradeThesis ?? '')}</textarea>
    </div>
    <div class="form-group">
      <label class="form-label">Notes <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <textarea id="trade-notes" rows="2" style="resize:vertical" placeholder="Any other context — not the thesis or the review, just notes">${esc(prefill.notes ?? '')}</textarea>
    </div>
    <div class="form-group">
      <label class="form-label">Tags <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <input type="text" id="trade-tags" value="${esc(prefill.tags ?? '')}" placeholder="e.g. earnings, swing, high-conviction">
      <div class="field-helper">Comma-separated.</div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Fees ($) <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
        <input type="number" id="trade-fees" value="${prefill.fees ?? ''}" step="0.01" placeholder="0.00" style="font-family:var(--font-mono)">
      </div>
      <div class="form-group">
        <label class="form-label">Stop loss <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
        <input type="number" id="trade-stoploss" value="${prefill.stopLossPrice ?? ''}" step="0.01" placeholder="Price" style="font-family:var(--font-mono)">
        <div class="field-helper">Used to compute R-multiple.</div>
      </div>
    </div>
    <div id="trade-err" class="auth-error"></div>
    <div style="display:flex;gap:var(--space-3)">
      <button class="btn btn-primary w-full" id="save-trade-btn" style="justify-content:center;padding:12px">${prefill.id ? 'Save changes' : 'Add trade'}</button>
      ${prefill.id ? `<button class="btn btn-danger" id="delete-trade-btn" style="padding:12px 16px">Delete</button>` : ''}
    </div>`;

  content.querySelector('#trade-type').addEventListener('change', (e) => {
    content.querySelector('#option-fields').style.display = e.target.value === 'stock' ? 'none' : 'block';
    updatePreview();
  });

  content.querySelector('#trade-status').addEventListener('change', (e) => {
    content.querySelector('#close-fields').style.display = e.target.value === 'open' ? 'none' : 'block';
    updatePreview();
  });

  // ── Fill rows (shared renderer for entries and exits) ────────────────────
  function renderFillRows(kind) {
    const rows      = kind === 'entry' ? entryFills : exitFills;
    const container = content.querySelector(`#trade-${kind}-fills`);
    container.innerHTML = rows.map((r, i) => `
      <div class="form-row trade-fill-row" data-kind="${kind}" data-idx="${i}" style="margin-bottom:var(--space-2);align-items:flex-start">
        <input type="date" class="trade-fill-date" data-kind="${kind}" data-idx="${i}" value="${r.filledAt}" style="flex:1">
        <input type="number" class="trade-fill-price" data-kind="${kind}" data-idx="${i}" value="${r.price}" placeholder="Price" step="0.01" style="flex:1;font-family:var(--font-mono)">
        <input type="number" class="trade-fill-qty" data-kind="${kind}" data-idx="${i}" value="${r.quantity}" placeholder="Qty" min="1" step="1" style="flex:1;font-family:var(--font-mono)">
        ${rows.length > (kind === 'entry' ? 1 : 0) ? `<button type="button" class="btn btn-ghost trade-fill-remove" data-kind="${kind}" data-idx="${i}" style="padding:8px;flex-shrink:0">✕</button>` : ''}
      </div>`).join('');

    container.querySelectorAll('.trade-fill-date').forEach(input => {
      input.addEventListener('input', () => { rows[+input.dataset.idx].filledAt = input.value; updatePreview(); });
    });
    container.querySelectorAll('.trade-fill-price').forEach(input => {
      input.addEventListener('input', () => { rows[+input.dataset.idx].price = parseFloat(input.value) || 0; updatePreview(); });
    });
    container.querySelectorAll('.trade-fill-qty').forEach(input => {
      input.addEventListener('input', () => { rows[+input.dataset.idx].quantity = parseInt(input.value) || 0; updatePreview(); });
    });
    container.querySelectorAll('.trade-fill-remove').forEach(btn => {
      btn.addEventListener('click', () => { rows.splice(+btn.dataset.idx, 1); renderFillRows(kind); updatePreview(); });
    });
  }
  renderFillRows('entry');
  renderFillRows('exit');

  content.querySelector('#trade-add-entry').addEventListener('click', () => {
    entryFills.push({ price: '', quantity: 1, filledAt: today });
    renderFillRows('entry'); updatePreview();
  });
  content.querySelector('#trade-add-exit').addEventListener('click', () => {
    exitFills.push({ price: '', quantity: 1, filledAt: today });
    renderFillRows('exit'); updatePreview();
  });

  // Live P&L preview — mirrors calcTradePnL exactly: options are ×100
  // (per-share price representing a 100-share contract), stock fills are 1:1,
  // and fees are subtracted the same way the backend's netPnl does.
  function updatePreview() {
    const MULT = content.querySelector('#trade-type').value === 'option' ? 100 : 1;
    const fees     = parseFloat(content.querySelector('#trade-fees')?.value) || 0;
    const cost     = entryFills.reduce((s, f) => s + (parseFloat(f.price) || 0) * (parseInt(f.quantity) || 0) * MULT, 0);
    const proceeds = exitFills.reduce((s, f) => s + (parseFloat(f.price) || 0) * (parseInt(f.quantity) || 0) * MULT, 0);
    const isClosing = content.querySelector('#trade-status').value !== 'open';
    const el = content.querySelector('#trade-pnl-preview');
    if (!isClosing || exitFills.length === 0) {
      el.innerHTML = `<div style="font-size:.75rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.05em">Cost so far</div>
        <div class="font-mono" style="font-size:1.1rem;font-weight:700;margin-top:2px">${fmt$(cost)}</div>`;
      return;
    }
    const pnl = proceeds - cost - fees;
    // % is gross-based (matches the backend's calcTradePnL.pnlPct exactly,
    // fees only affect the dollar figure) — computing it from the net dollar
    // amount instead would show a different percentage here than the Trade
    // Detail Sheet shows after saving the same trade, for no principled
    // reason (found live: 45% here vs. the saved trade's 50%).
    const grossPnl = proceeds - cost;
    const pnlPct = cost > 0 ? (grossPnl / cost) * 100 : 0;
    el.innerHTML = `<div style="font-size:.75rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.05em">Estimated P&amp;L${fees ? ' (after fees)' : ''}</div>
      <div class="font-mono" style="font-size:1.1rem;font-weight:700;margin-top:2px;color:${pnl >= 0 ? 'var(--positive)' : 'var(--negative)'}">${pnl >= 0 ? '+' : ''}${fmt$(pnl)} (${pnlPct.toFixed(1)}%)</div>`;
  }
  updatePreview();
  content.querySelector('#trade-fees')?.addEventListener('input', updatePreview);

  content.querySelector('#save-trade-btn').addEventListener('click', async () => {
    const errEl = content.querySelector('#trade-err');
    errEl.classList.remove('show');
    const ticker       = content.querySelector('#trade-ticker').value.trim().toUpperCase();
    const tradeType    = content.querySelector('#trade-type').value;
    const optionType   = tradeType === 'option' ? content.querySelector('#trade-opt-type').value : undefined;
    const strikePrice  = parseFloat(content.querySelector('#trade-strike')?.value) || undefined;
    const expiryDate   = content.querySelector('#trade-expiry')?.value || undefined;
    const status       = content.querySelector('#trade-status').value;
    const openedAt     = content.querySelector('#trade-opened').value;
    const preTradeThesis = content.querySelector('#trade-thesis').value.trim() || undefined;
    const notes        = content.querySelector('#trade-notes').value.trim() || undefined;
    const tags         = content.querySelector('#trade-tags').value.trim() || undefined;
    const fees         = parseFloat(content.querySelector('#trade-fees').value) || undefined;
    const stopLossPrice = parseFloat(content.querySelector('#trade-stoploss').value) || undefined;

    if (!ticker)   { errEl.textContent = 'Ticker required';      errEl.classList.add('show'); return; }
    if (!openedAt) { errEl.textContent = 'Open date required';   errEl.classList.add('show'); return; }
    if (entryFills.length === 0 || entryFills.some(f => !f.price || !f.quantity || !f.filledAt)) {
      errEl.textContent = 'At least one complete entry fill (date, price, qty) is required'; errEl.classList.add('show'); return;
    }
    if (status !== 'open' && exitFills.some(f => !f.price || !f.quantity || !f.filledAt)) {
      errEl.textContent = 'Exit fills need a date, price, and qty — or remove the incomplete row'; errEl.classList.add('show'); return;
    }

    const fills = [
      ...entryFills.map(f => ({ side: 'buy', price: f.price, quantity: f.quantity, filledAt: f.filledAt })),
      ...(status !== 'open' ? exitFills.filter(f => f.price && f.quantity && f.filledAt).map(f => ({ side: 'sell', price: f.price, quantity: f.quantity, filledAt: f.filledAt })) : []),
    ];
    const quantity = entryFills.reduce((s, f) => s + (parseInt(f.quantity) || 0), 0);

    try {
      if (prefill.id) {
        const updateParams = { status, preTradeThesis, notes, tags, fees, stopLossPrice, fills };
        if (status !== 'open') {
          updateParams.closedAt = content.querySelector('#trade-closed-date').value || undefined;
          updateParams.thesisAccuracy = parseInt(content.querySelector('#trade-accuracy').value) || undefined;
          updateParams.postTradeReview = content.querySelector('#trade-review').value.trim() || undefined;
        }
        await api.trades.update(prefill.id, updateParams);
      } else {
        const closedAt = status !== 'open' ? (content.querySelector('#trade-closed-date').value || undefined) : undefined;
        await api.trades.create({ ticker, tradeType, optionType, strikePrice, expiryDate, quantity, status, closedAt, openedAt, preTradeThesis, notes, tags, fees, stopLossPrice, fills });
      }
      closeSheet();
      showToast(prefill.id ? 'Trade updated' : 'Trade logged');
      if (rendered.has('progress')) { renderTab('progress'); }
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed';
      errEl.classList.add('show');
    }
  });

  content.querySelector('#delete-trade-btn')?.addEventListener('click', async () => {
    if (!confirm('Delete this trade?')) return;
    await api.trades.delete(prefill.id).catch(() => {});
    closeSheet();
    showToast('Trade deleted');
    rendered.delete('progress'); renderTab('progress');
  });

  presentSheet();
}

// ── TRADE DETAIL SHEET ─────────────────────────────────────────────────────
// A read-only view distinct from openTradeSheet (the create/edit form) — shows
// the entry/exit fills list, P&L, thesis/review, and tags at a glance, with an
// Edit button that hands off to the existing form for changes.
export function openTradeDetailSheet(trade) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  const isClosed  = trade.status === 'closed' || trade.status === 'expired_worthless';
  // netPnl already equals grossPnl when there are no fees — always safe to
  // show as "the" P&L rather than branching on whether fees were entered.
  const pnlAmt    = isClosed ? (trade.pnl?.netPnl ?? trade.pnl?.grossPnl ?? 0) : null;
  const pnlColor  = pnlAmt != null ? (pnlAmt >= 0 ? 'var(--positive)' : 'var(--negative)') : 'var(--text-secondary)';
  const typeLabel = trade.tradeType === 'option' ? `${trade.optionType?.toUpperCase()} $${trade.strikePrice}` : 'Stock';
  const tags      = (trade.tags ?? '').split(',').map(t => t.trim()).filter(Boolean);
  const entries   = (trade.fills ?? []).filter(f => f.side === 'buy');
  const exits     = (trade.fills ?? []).filter(f => f.side === 'sell');

  const fillRow = (f) => `
    <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--bg-tertiary);font-size:.8rem">
      <span style="color:var(--text-secondary)">${esc(fmtDate(f.filledAt))}</span>
      <span class="font-mono">${f.quantity} @ ${fmt$(f.price, 2)}</span>
    </div>`;

  content.innerHTML = `
    <div class="sheet-title" style="display:flex;align-items:center;gap:8px">
      ${esc(trade.ticker)}
      <span style="font-size:.75rem;font-weight:400;color:var(--text-tertiary);padding:2px 8px;background:var(--bg-tertiary);border-radius:4px">${typeLabel}</span>
    </div>
    <div style="margin:var(--space-2) 0 var(--space-4)">
      ${pnlAmt != null
        ? `<span class="font-mono" style="font-weight:700;font-size:1.1rem;color:${pnlColor}">${pnlAmt >= 0 ? '+' : ''}${fmt$(pnlAmt)} (${trade.pnl?.pnlPct?.toFixed(1) ?? 0}%)</span>`
        : `<span style="font-size:.85rem;color:var(--accent);font-weight:600;padding:3px 10px;background:rgba(82,106,240,0.1);border-radius:4px">OPEN</span>`}
      ${trade.fees ? `<div style="font-size:.75rem;color:var(--text-tertiary);margin-top:2px">Fees: ${fmt$(trade.fees, 2)}</div>` : ''}
      ${trade.rMultiple != null ? `<div style="font-size:.75rem;color:var(--text-tertiary);margin-top:2px">R-multiple: <span class="font-mono" style="color:${trade.rMultiple >= 0 ? 'var(--positive)' : 'var(--negative)'}">${trade.rMultiple >= 0 ? '+' : ''}${trade.rMultiple.toFixed(2)}R</span></div>` : ''}
    </div>

    ${tags.length ? `
    <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:var(--space-4)">
      ${tags.map(t => `<span style="font-size:.7rem;color:var(--accent);background:rgba(82,106,240,0.1);padding:2px 8px;border-radius:99px">${esc(t)}</span>`).join('')}
    </div>` : ''}

    <div class="form-group">
      <label class="form-label">Entry fills</label>
      ${entries.length ? entries.map(fillRow).join('') : `<p class="color-tertiary text-sm">None recorded.</p>`}
    </div>
    ${exits.length ? `
    <div class="form-group">
      <label class="form-label">Exit fills</label>
      ${exits.map(fillRow).join('')}
    </div>` : ''}

    ${trade.preTradeThesis ? `
    <div class="form-group">
      <label class="form-label">Pre-trade thesis</label>
      <div style="font-size:.85rem;color:var(--text-secondary);padding:8px;background:var(--bg-tertiary);border-radius:var(--radius-sm);border-left:2px solid var(--accent)">${esc(trade.preTradeThesis)}</div>
    </div>` : ''}
    ${trade.postTradeReview ? `
    <div class="form-group">
      <label class="form-label">Post-trade review</label>
      <div style="font-size:.85rem;color:var(--text-secondary);padding:8px;background:var(--bg-tertiary);border-radius:var(--radius-sm);border-left:2px solid var(--positive)">
        ${esc(trade.postTradeReview)}
        ${trade.thesisAccuracy != null ? ` <span style="color:var(--positive)">· Accuracy: ${trade.thesisAccuracy}/10</span>` : ''}
      </div>
    </div>` : ''}
    ${trade.notes ? `
    <div class="form-group">
      <label class="form-label">Notes</label>
      <div style="font-size:.85rem;color:var(--text-secondary);padding:8px;background:var(--bg-tertiary);border-radius:var(--radius-sm)">${esc(trade.notes)}</div>
    </div>` : ''}

    <button class="btn btn-primary w-full" id="edit-trade-from-detail-btn" style="justify-content:center;padding:12px;margin-top:var(--space-2)">Edit trade</button>`;

  content.querySelector('#edit-trade-from-detail-btn').addEventListener('click', () => {
    openTradeSheet(trade);
  });

  presentSheet();
}

export function openRothSheet(editContribution = null) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const today   = new Date().toISOString().split('T')[0];
  const r       = editContribution;
  // Keep an edited contribution's original year (the form doesn't ask for it);
  // new contributions belong to the current calendar year.
  const year    = r ? r.year : new Date().getFullYear();

  content.innerHTML = `
    <div class="sheet-title">${r ? 'Edit' : 'Log'} Roth IRA contribution</div>
    <div class="form-group">
      <label class="form-label">Amount ($)</label>
      <input type="number" id="roth-amount" placeholder="0.00" step="0.01" value="${r ? esc(String(r.amount)) : ''}" style="font-family:var(--font-mono)" autofocus>
      <div class="field-helper">$7,000 max for ${year}. You can make multiple contributions throughout the year.</div>
    </div>
    <div class="form-group">
      <label class="form-label">Date</label>
      <input type="date" id="roth-date" value="${r ? esc(r.date) : today}">
    </div>
    <div class="form-group">
      <label class="form-label">Notes (optional)</label>
      <input type="text" id="roth-notes" placeholder="e.g. January contribution" value="${r && r.notes ? esc(r.notes) : ''}">
    </div>
    <div id="roth-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="save-roth-btn" style="justify-content:center;padding:12px;margin-top:var(--space-2)">${r ? 'Save changes' : 'Save contribution'}</button>`;

  content.querySelector('#save-roth-btn').addEventListener('click', async () => {
    const errEl = content.querySelector('#roth-err');
    errEl.classList.remove('show');
    const amount = parseFloat(content.querySelector('#roth-amount').value);
    const date   = content.querySelector('#roth-date').value;
    const notes  = content.querySelector('#roth-notes').value.trim() || null;
    if (!amount || amount <= 0) { errEl.textContent = 'Valid amount required'; errEl.classList.add('show'); return; }
    try {
      const body = { amount, year, contributionDate: date, notes };
      // FABLE5_SHIP_AUDIT F-6: the API now returns a `warning` string when this
      // year's total (including the row just saved) exceeds the real IRS
      // limit — surface it as a second toast rather than silently letting the
      // Progress tab's "X / $7,000" card be the only sign anything's off.
      const saved = r ? await api.roth.update(r.id, body) : await api.post('/api/roth', body);
      closeSheet();
      showToast(r ? 'Contribution updated' : 'Contribution logged');
      if (saved?.warning) showToast(saved.warning);
      if (rendered.has('progress')) { renderTab('progress'); }
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed';
      errEl.classList.add('show');
    }
  });

  presentSheet();
}

// ── SHARED INCOME / EXPENSE SHEET ─────────────────────────────────────────

function _openFinanceSheet(type, prefill = {}) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const today   = new Date().toISOString().split('T')[0];

  if (type === 'income') {
    content.innerHTML = `
      <div class="sheet-title">Log income</div>
      <div class="form-group">
        <label class="form-label">Type</label>
        <select id="inc-type">
          <option value="salary"     ${(prefill.type ?? 'salary') === 'salary'     ? 'selected' : ''}>Paycheck (salary/hourly)</option>
          <option value="commission" ${prefill.type === 'commission'               ? 'selected' : ''}>Commission</option>
          <option value="other"      ${prefill.type === 'other'                    ? 'selected' : ''}>Other income</option>
        </select>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Net amount ($)</label>
          <input type="number" id="inc-amount" value="${prefill.amount ?? ''}" step="0.01" placeholder="0.00" style="font-family:var(--font-mono)">
        </div>
        <div class="form-group">
          <label class="form-label">Pay date</label>
          <input type="date" id="inc-date" value="${prefill.payDate ?? today}">
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Notes</label>
        <input type="text" id="inc-notes" value="${esc(prefill.notes ?? '')}" placeholder="Optional">
      </div>
      <div id="inc-err" class="auth-error"></div>
      <div style="display:flex;gap:var(--space-3);margin-top:var(--space-2)">
        <button class="btn btn-primary w-full" id="save-inc-btn" style="justify-content:center;padding:12px">${prefill.id ? 'Save changes' : 'Save income'}</button>
        ${prefill.id ? `<button class="btn btn-danger" id="delete-inc-btn" style="padding:12px 16px">Delete</button>` : ''}
      </div>`;

    content.querySelector('#save-inc-btn').addEventListener('click', async () => {
      const errEl  = content.querySelector('#inc-err');
      errEl.classList.remove('show');
      const type   = content.querySelector('#inc-type').value;
      const amount = parseFloat(content.querySelector('#inc-amount').value);
      const payDate= content.querySelector('#inc-date').value;
      const notes  = content.querySelector('#inc-notes').value.trim() || null;
      if (!amount || amount <= 0) { errEl.textContent = 'Valid amount required'; errEl.classList.add('show'); return; }
      try {
        if (prefill.id) { await api.income.update(prefill.id, { type, amount, isGross: false, payDate, notes }); }
        else            { await api.income.create({ type, amount, isGross: false, payDate, notes }); }
        closeSheet();
        showToast(prefill.id ? 'Income updated' : 'Income logged');
        if (rendered.has('pulse'))    { renderTab('pulse'); }
        if (rendered.has('progress')) { renderTab('progress'); }
      } catch (err) { errEl.textContent = err.message ?? 'Failed'; errEl.classList.add('show'); }
    });

    content.querySelector('#delete-inc-btn')?.addEventListener('click', async () => {
      if (!confirm('Delete this income entry?')) return;
      await api.income.delete(prefill.id).catch(() => {});
      closeSheet();
      showToast('Income deleted');
      if (rendered.has('pulse'))    { renderTab('pulse'); }
      if (rendered.has('progress')) { renderTab('progress'); }
    });

  } else {
    // Expense
    let cats = [];
    api.categories().then(c => {
      cats = c;
      const sel = content.querySelector('#exp-cat');
      if (sel) sel.innerHTML = `<option value="">No category</option>${cats.map(c => `<option value="${esc(c.id)}">${esc(c.name)}</option>`).join('')}`;
    }).catch(() => {});

    content.innerHTML = `
      <div class="sheet-title">Log expense</div>
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Amount ($) *</label>
          <input type="number" id="exp-amount" value="${prefill.amount ?? ''}" step="0.01" placeholder="0.00" style="font-family:var(--font-mono)" autofocus>
        </div>
        <div class="form-group">
          <label class="form-label">Date</label>
          <input type="date" id="exp-date" value="${prefill.date ?? today}">
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Description</label>
        <input type="text" id="exp-name" value="${esc(prefill.name ?? '')}" placeholder="What was this for?">
      </div>
      <div class="form-group">
        <label class="form-label">Category</label>
        <select id="exp-cat"><option value="">Loading…</option></select>
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:var(--space-2);cursor:pointer;font-size:.875rem">
          <input type="checkbox" id="exp-recurring" ${prefill.isRecurring ? 'checked' : ''}> Recurring expense
        </label>
        <div class="field-helper">Recurring expenses count toward every future occurrence's budget automatically.</div>
      </div>
      <div class="form-group" id="exp-frequency-wrap" style="display:${prefill.isRecurring ? 'block' : 'none'}">
        <label class="form-label">Frequency</label>
        <select id="exp-frequency">
          <option value="" ${!prefill.recurringIntervalDays ? 'selected' : ''}>Monthly (calendar month)</option>
          <option value="7"   ${prefill.recurringIntervalDays === 7   ? 'selected' : ''}>Weekly</option>
          <option value="91"  ${prefill.recurringIntervalDays === 91  ? 'selected' : ''}>Quarterly</option>
          <option value="365" ${prefill.recurringIntervalDays === 365 ? 'selected' : ''}>Annual</option>
        </select>
      </div>
      <div class="form-group" id="exp-until-wrap" style="display:${prefill.isRecurring ? 'block' : 'none'}">
        <label class="form-label">Repeat until <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
        <input type="date" id="exp-recurring-end" value="${esc(prefill.recurringEndDate ?? '')}">
        <div class="field-helper">Leave blank if this expense never ends (e.g. rent).</div>
      </div>
      <div class="form-group">
        <label class="form-label">Notes <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
        <input type="text" id="exp-notes" value="${esc(prefill.notes ?? '')}" placeholder="Optional">
      </div>
      <div id="exp-err" class="auth-error"></div>
      <div style="display:flex;gap:var(--space-3)">
        <button class="btn btn-primary w-full" id="save-exp-btn" style="justify-content:center;padding:12px">${prefill.id ? 'Save changes' : 'Add expense'}</button>
        ${prefill.id ? `<button class="btn btn-danger" id="delete-exp-btn" style="padding:12px 16px">Delete</button>` : ''}
      </div>`;

    // Re-inject categories — the MONEY taxonomy (#63 / UX_AUDIT B-4), not the
    // Work/School/Growth/Fun time categories this used to offer. Filing rent under
    // "Growth" is what made the Budget view unable to answer "what do I spend on
    // housing?"; the legacy categoryId is no longer written by this sheet.
    api.expenseCategories().then(c => {
      const sel = content.querySelector('#exp-cat');
      if (sel) {
        sel.innerHTML = `<option value="">No category</option>${c.map(cat => `<option value="${esc(cat.id)}">${esc(cat.name)}</option>`).join('')}`;
        if (prefill.expenseCategoryId) sel.value = prefill.expenseCategoryId;
      }
    }).catch(() => {});

    // Toggle 'until' date + frequency when recurring checkbox changes
    content.querySelector('#exp-recurring')?.addEventListener('change', (e) => {
      const until = content.querySelector('#exp-until-wrap');
      const freq  = content.querySelector('#exp-frequency-wrap');
      if (until) until.style.display = e.target.checked ? 'block' : 'none';
      if (freq)  freq.style.display  = e.target.checked ? 'block' : 'none';
    });

    content.querySelector('#save-exp-btn').addEventListener('click', async () => {
      const errEl      = content.querySelector('#exp-err');
      errEl.classList.remove('show');
      const amount     = parseFloat(content.querySelector('#exp-amount').value);
      const date       = content.querySelector('#exp-date').value;
      const name       = content.querySelector('#exp-name').value.trim() || 'Expense';
      const expenseCategoryId = content.querySelector('#exp-cat').value || null;
      const isRecurring       = content.querySelector('#exp-recurring').checked;
      const recurringEndDate   = content.querySelector('#exp-recurring-end')?.value || null;
      const recurringIntervalDays = isRecurring ? (parseInt(content.querySelector('#exp-frequency').value) || null) : null;
      const notes             = content.querySelector('#exp-notes').value.trim() || null;
      if (!amount || amount <= 0) { errEl.textContent = 'Valid amount required'; errEl.classList.add('show'); return; }
      try {
        if (prefill.id) { await api.expenses.update(prefill.id, { amount, date, name, expenseCategoryId, isRecurring, recurringEndDate, recurringIntervalDays, notes }); }
        else            { await api.expenses.create({ amount, date, name, expenseCategoryId, isRecurring, recurringEndDate, recurringIntervalDays, notes }); }
        closeSheet();
        showToast(prefill.id ? 'Expense updated' : 'Expense logged');
        if (rendered.has('pulse'))    { renderTab('pulse'); }
        if (rendered.has('progress')) { renderTab('progress'); }
        if (rendered.has('settings')) { renderTab('settings'); } // Recurring Expenses list
      } catch (err) { errEl.textContent = err.message ?? 'Failed'; errEl.classList.add('show'); }
    });

    content.querySelector('#delete-exp-btn')?.addEventListener('click', async () => {
      if (!confirm('Delete this expense?')) return;
      await api.expenses.delete(prefill.id).catch(() => {});
      closeSheet();
      showToast('Expense deleted');
      rendered.delete('progress'); renderTab('progress');
      if (rendered.has('settings')) { renderTab('settings'); } // Recurring Expenses list
    });
  }

  presentSheet();
}

// ── WEEKLY REVIEW SHEET ────────────────────────────────────────────────────

function getBlockHoursForReview(b) {
  if (!b.startTime || !b.endTime) return 0;
  const [sh, sm] = b.startTime.split(':').map(Number);
  const [eh, em] = b.endTime.split(':').map(Number);
  return Math.max(0, (eh + em / 60) - (sh + sm / 60));
}

export async function openWeeklyReviewSheet() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  const { weekStart, weekEnd } = getWeekBounds();

  content.innerHTML = `<div class="sheet-title">Weekly review</div><p class="color-tertiary text-sm">Loading this week's summary…</p>`;
  presentSheet();

  const [tasks, blocks, income, expenses, pastReviews] = await Promise.all([
    api.tasks.list().catch(() => []),
    api.blocks.list().catch(() => []),
    api.income.list().catch(() => []),
    api.expenses.list().catch(() => []),
    api.weeklyReviews.list().catch(() => []),
  ]);

  const inRange = (d) => d && d >= weekStart && d <= weekEnd;
  const tasksCompleted = tasks.filter(t => t.status === 'done' && inRange(t.completedAt)).length;
  const hoursLogged = blocks
    .filter(b => inRange(b.date))
    .reduce((sum, b) => sum + getBlockHoursForReview(b), 0);
  const incomeTotal = income
    .filter(i => inRange(i.payDate))
    .reduce((sum, i) => sum + i.amount, 0);
  const expensesTotal = expenses
    .filter(e => inRange(e.date))
    .reduce((sum, e) => sum + e.amount, 0);

  const existing = pastReviews.find(r => r.weekStart === weekStart);

  content.innerHTML = `
    <div class="sheet-title">Weekly review</div>
    <div class="field-helper" style="margin-top:-8px;margin-bottom:var(--space-4)">${fmtWeekRange(weekStart, weekEnd)}</div>

    <div class="card" style="margin-bottom:var(--space-4);display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3)">
      <div>
        <div style="font-size:1.2rem;font-weight:700">${tasksCompleted}</div>
        <div style="font-size:.7rem;color:var(--text-tertiary)">Tasks completed</div>
      </div>
      <div>
        <div style="font-size:1.2rem;font-weight:700">${hoursLogged.toFixed(1)}h</div>
        <div style="font-size:.7rem;color:var(--text-tertiary)">Hours logged</div>
      </div>
      <div>
        <div style="font-size:1.2rem;font-weight:700;color:var(--positive)">${fmt$(incomeTotal)}</div>
        <div style="font-size:.7rem;color:var(--text-tertiary)">Income</div>
      </div>
      <div>
        <div style="font-size:1.2rem;font-weight:700;color:var(--negative)">${fmt$(expensesTotal)}</div>
        <div style="font-size:.7rem;color:var(--text-tertiary)">Expenses</div>
      </div>
    </div>

    <div class="form-group">
      <label class="form-label">Wins this week</label>
      <textarea id="review-wins" placeholder="What went well?" style="height:70px;padding:8px;font-size:.85rem;resize:none">${esc(existing?.wins ?? '')}</textarea>
    </div>
    <div class="form-group">
      <label class="form-label">Challenges</label>
      <textarea id="review-challenges" placeholder="What was hard?" style="height:70px;padding:8px;font-size:.85rem;resize:none">${esc(existing?.challenges ?? '')}</textarea>
    </div>
    <div class="form-group">
      <label class="form-label">Focus for next week</label>
      <textarea id="review-next" placeholder="What matters most next week?" style="height:70px;padding:8px;font-size:.85rem;resize:none">${esc(existing?.nextWeekFocus ?? '')}</textarea>
    </div>

    <div class="form-group">
      <label class="form-label">Energy level</label>
      <div style="display:flex;align-items:center;gap:var(--space-3)">
        <input type="range" id="review-energy" min="1" max="10" step="1" value="${existing?.energyLevel ?? 5}" style="flex:1;accent-color:var(--accent)">
        <span class="font-mono" style="font-weight:700;width:24px;text-align:right" id="review-energy-disp">${existing?.energyLevel ?? 5}</span>
      </div>
    </div>
    <div class="form-group">
      <label class="form-label">Financial confidence</label>
      <div style="display:flex;align-items:center;gap:var(--space-3)">
        <input type="range" id="review-financial" min="1" max="10" step="1" value="${existing?.financialScore ?? 5}" style="flex:1;accent-color:var(--accent)">
        <span class="font-mono" style="font-weight:700;width:24px;text-align:right" id="review-financial-disp">${existing?.financialScore ?? 5}</span>
      </div>
    </div>

    <div id="review-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="save-review-btn" style="justify-content:center;padding:12px;margin-top:var(--space-2)">${existing ? 'Save changes' : 'Save review'}</button>`;

  content.querySelector('#review-energy').addEventListener('input', (e) => {
    content.querySelector('#review-energy-disp').textContent = e.target.value;
  });
  content.querySelector('#review-financial').addEventListener('input', (e) => {
    content.querySelector('#review-financial-disp').textContent = e.target.value;
  });

  content.querySelector('#save-review-btn').addEventListener('click', async () => {
    const errEl = content.querySelector('#review-err');
    errEl.classList.remove('show');
    const wins           = content.querySelector('#review-wins').value.trim() || null;
    const challenges      = content.querySelector('#review-challenges').value.trim() || null;
    const nextWeekFocus   = content.querySelector('#review-next').value.trim() || null;
    const energyLevel     = parseInt(content.querySelector('#review-energy').value);
    const financialScore  = parseInt(content.querySelector('#review-financial').value);
    try {
      await api.weeklyReviews.save({ weekStart, weekEnd, wins, challenges, nextWeekFocus, energyLevel, financialScore });
      // Re-render (not just rendered.has check) so the banner disappears
      // immediately — Pulse now gates it on `reviewedThisWeek`, but that flag
      // was computed before this save, so the old render still shows it.
      rendered.delete('pulse'); renderTab('pulse');
      // Transition straight into Planning Mode in the same sheet, rather than
      // closing — "post-weekly-review" planning is the whole point of a
      // review, not a separate thing the user has to go find afterward.
      openPlanningModeSheet();
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to save';
      errEl.classList.add('show');
    }
  });
}

function fmtWeekRange(start, end) {
  const opts = { month: 'short', day: 'numeric' };
  const s = new Date(start + 'T12:00:00').toLocaleDateString('en-US', opts);
  const e = new Date(end + 'T12:00:00').toLocaleDateString('en-US', opts);
  return `${s} – ${e}`;
}

// ── SCHEDULE ASSISTANT ─────────────────────────────────────────────────────
// Ranked-slot scheduling, matching inspo-images/pulse-schedule-assistant.png:
// given a task, find open gaps in this week's actual schedule (real blocks +
// school blocks + recurring-rule instances) big enough for it, rank them
// (same-category day > soonest > larger gap), and let the user commit one
// with a single tap — no new backend endpoint, this reads the same
// `api.blocks.*` list calls Pulse/Plan already use.

function timeToMin(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}
function minToTimeHHMM(min) {
  const h = Math.floor(min / 60), m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function fmtTime12(min) {
  const h = Math.floor(min / 60) % 24, m = min % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}
function fmtSlotDayLabel(dateStr, todayStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
  const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const tmrw = new Date(todayStr + 'T12:00:00'); tmrw.setDate(tmrw.getDate() + 1);
  if (dateStr === todayStr) return `Tonight, ${dayName} ${monthDay}`;
  if (dateStr === tmrw.toISOString().split('T')[0]) return `Tomorrow, ${dayName} ${monthDay}`;
  return `${dayName}, ${monthDay}`;
}

// Pure function — easy to test/verify independent of the DOM. `allBusy` is
// the merged list of this week's real/school/recurring blocks (date/
// startTime/endTime/categoryId shape, matching what api.blocks.* returns).
// `wakingSegments` (from getWakingSegments()) is one or two {start,end}
// minute-of-day ranges to search within — two when the user's sleep window
// wraps midnight (night-shift/night-class), never a hardcoded blanket
// window, so this doesn't assume everyone's awake day starts in the morning.
// `sleepStartTime`/`sleepDurationHours` are passed through to
// matchesTimePreference so a task's morning/afternoon/night preference
// (KNOWN_INCONSISTENCIES #26 Phase 3) is judged against the user's own
// waking window, not literal clock hours — same settings wakingSegments
// was derived from.
function findScheduleSlots(task, allBusy, todayStr, weekEndStr, wakingSegments, sleepStartTime, sleepDurationHours) {
  const neededHours = task.canSplit && task.minSessionHours
    ? Math.min(task.minSessionHours, task.estimatedHours ?? task.minSessionHours)
    : task.estimatedHours;
  if (!neededHours) return [];
  const neededMin = Math.round(neededHours * 60);

  const days = [];
  for (let d = todayStr; d <= weekEndStr;) {
    days.push(d);
    const next = new Date(d + 'T12:00:00'); next.setDate(next.getDate() + 1);
    d = next.toISOString().split('T')[0];
  }

  const nowMin = (() => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); })();
  const slots = [];

  for (const day of days) {
    const dayBusyAll = allBusy
      .filter(b => b.date === day && b.startTime && b.endTime)
      .map(b => ({ start: timeToMin(b.startTime), end: timeToMin(b.endTime), categoryId: b.categoryId }))
      .sort((a, b) => a.start - b.start);
    const dayHasCategoryMatch = task.categoryId != null && dayBusyAll.some(b => b.categoryId === task.categoryId);

    for (const seg of wakingSegments) {
      let cursor = seg.start;
      if (day === todayStr) cursor = Math.max(cursor, nowMin);
      if (cursor >= seg.end) continue; // this segment has already passed today

      const segBusy = dayBusyAll.filter(b => b.end > seg.start && b.start < seg.end);
      for (const b of segBusy) {
        const bStart = Math.max(b.start, seg.start), bEnd = Math.min(b.end, seg.end);
        if (bStart > cursor) {
          const gapLen = bStart - cursor;
          if (gapLen >= neededMin) {
            const matchesPreference = matchesTimePreference(cursor, task, sleepStartTime, sleepDurationHours);
            slots.push({ date: day, startMin: cursor, endMin: cursor + neededMin, gapLen, dayHasCategoryMatch, matchesPreference });
          }
        }
        cursor = Math.max(cursor, bEnd);
      }
      if (seg.end - cursor >= neededMin) {
        const matchesPreference = matchesTimePreference(cursor, task, sleepStartTime, sleepDurationHours);
        slots.push({ date: day, startMin: cursor, endMin: cursor + neededMin, gapLen: seg.end - cursor, dayHasCategoryMatch, matchesPreference });
      }
    }
  }

  slots.sort((a, b) => {
    if (a.matchesPreference !== b.matchesPreference) return a.matchesPreference ? -1 : 1;
    if (a.dayHasCategoryMatch !== b.dayHasCategoryMatch) return a.dayHasCategoryMatch ? -1 : 1;
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    return b.gapLen - a.gapLen;
  });

  return slots.slice(0, 5).map((s, i) => ({ ...s, isBest: i === 0 }));
}

export async function openScheduleAssistantSheet(task) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  content.innerHTML = `<div class="sheet-title">Schedule this task</div><p class="color-tertiary text-sm">Finding open time…</p>`;
  presentSheet();

  if (!task.estimatedHours) {
    content.innerHTML = `
      <div class="sheet-title">Schedule this task</div>
      <p class="color-tertiary text-sm" style="padding:var(--space-4) 0">
        Add estimated hours to "${esc(task.title)}" first so the assistant knows how much time to look for.
      </p>
      <button class="btn btn-primary w-full" id="sched-edit-task-btn" style="justify-content:center;padding:12px">Edit task</button>`;
    content.querySelector('#sched-edit-task-btn').addEventListener('click', () => openAddTaskSheet(task));
    return;
  }

  const today = new Date().toISOString().split('T')[0];
  const { weekEnd } = getWeekBounds();

  const [blocks, schoolBlocks, recurringBlocks, userSettings] = await Promise.all([
    api.blocks.list({ from: today, to: weekEnd }).catch(() => []),
    api.blocks.schoolBlocks({ from: today, to: weekEnd }).catch(() => []),
    api.blocks.recurringBlocks({ from: today, to: weekEnd }).catch(() => []),
    api.settings.get().catch(() => null),
  ]);
  const allBusy = [...blocks.filter(b => !b.ruleId), ...schoolBlocks, ...recurringBlocks];
  const wakingSegments = getWakingSegments(userSettings?.sleepStartTime, userSettings?.sleepDurationHours);
  const slots = findScheduleSlots(task, allBusy, today, weekEnd, wakingSegments, userSettings?.sleepStartTime, userSettings?.sleepDurationHours);

  content.innerHTML = `
    <div class="sheet-title">Schedule this task</div>
    <div class="card" style="margin:var(--space-3) 0">
      <div style="font-weight:700;font-size:.95rem;margin-bottom:6px">${esc(task.title)}</div>
      <div style="display:flex;gap:8px;align-items:center;font-size:.8rem;color:var(--text-secondary);flex-wrap:wrap">
        <span>${task.estimatedHours}h estimated</span>
        ${task.dueDate ? `<span>· Due ${fmtDate(task.dueDate)}</span>` : ''}
      </div>
      ${!task.canSplit ? `<div style="margin-top:8px;font-size:.75rem;color:var(--text-tertiary)">ⓘ This task must be done in one sitting.</div>` : ''}
    </div>

    ${slots.length === 0 ? `
      <p class="color-tertiary text-sm" style="text-align:center;padding:var(--space-6) 0">No open slots found this week that fit ${task.estimatedHours}h. Try the manual scheduler instead.</p>
      <button class="btn btn-ghost w-full" id="sched-manual-btn" style="justify-content:center;padding:12px">Open manual scheduler</button>
    ` : `
      <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:var(--space-2)">Available slots this week</div>
      ${slots.map((s, i) => `
        <div class="card" style="margin-bottom:var(--space-3);${s.isBest ? 'border:2px solid var(--accent)' : ''}">
          ${s.isBest ? `<div style="font-size:.7rem;color:var(--accent);font-weight:700;margin-bottom:6px">★ BEST MATCH</div>` : ''}
          <div style="font-weight:600;font-size:.85rem">${esc(fmtSlotDayLabel(s.date, today))}</div>
          <div style="font-weight:800;font-size:1.1rem;margin:2px 0">${fmtTime12(s.startMin)} – ${fmtTime12(s.endMin)}</div>
          <div style="font-size:.75rem;color:var(--text-tertiary)">${(s.gapLen / 60).toFixed(1)}h open${s.matchesPreference && task.preferredTimeOfDay ? ' · preferred time' : ''}${s.dayHasCategoryMatch ? ' · category match' : ''}</div>
          <button class="btn ${s.isBest ? 'btn-primary' : 'btn-ghost'} w-full sched-pick-btn" data-idx="${i}" style="justify-content:center;padding:10px;margin-top:var(--space-3)">Schedule this slot</button>
        </div>`).join('')}
      <div id="sched-err" class="auth-error"></div>
    `}`;

  content.querySelector('#sched-manual-btn')?.addEventListener('click', () => {
    openAddBlockSheet({ title: task.title, taskId: task.id });
  });

  content.querySelectorAll('.sched-pick-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const s = slots[+btn.dataset.idx];
      const errEl = content.querySelector('#sched-err');
      try {
        await api.blocks.create({
          title:      task.title,
          date:       s.date,
          categoryId: task.categoryId ?? null,
          startTime:  minToTimeHHMM(s.startMin),
          endTime:    minToTimeHHMM(s.endMin),
          taskId:     task.id,
        });
        closeSheet();
        showToast('Task scheduled');
        if (rendered.has('plan'))  { rendered.delete('plan');  renderTab('plan'); }
        if (rendered.has('pulse')) { rendered.delete('pulse'); renderTab('pulse'); }
      } catch (err) {
        if (errEl) { errEl.textContent = err.message ?? 'Failed to schedule'; errEl.classList.add('show'); }
      }
    });
  });
}

// ── INBOX PROCESSING MODE ──────────────────────────────────────────────────
// One-at-a-time review of tasks with no category ("Inbox" — same definition
// plan.js's own Inbox filter already uses: no categoryId, not done), matching
// inspo-images/plan-split-view.png's "Inbox · N unprocessed → PROCESS" flow.
// A task leaves the inbox by getting a real category (the common case), or by
// being marked done/skipped for now — skip does NOT remove it from the real
// inbox, it just moves the review past it for this pass.
export async function openInboxProcessingSheet() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  content.innerHTML = `<div class="sheet-title">Process inbox</div><p class="color-tertiary text-sm">Loading…</p>`;
  presentSheet();

  const [allTasks, cats] = await Promise.all([
    api.tasks.list().catch(() => []),
    api.categories().catch(() => []),
  ]);
  const queue = allTasks.filter(t => !t.categoryId && t.status !== 'done');
  let index = 0;
  let processedCount = 0;

  function renderDone() {
    content.innerHTML = `
      <div class="sheet-title">Process inbox</div>
      <div style="text-align:center;padding:var(--space-8) 0">
        <div style="font-size:2rem;margin-bottom:var(--space-3)">🎉</div>
        <div style="font-weight:700;font-size:1.1rem;margin-bottom:4px">Inbox clear!</div>
        <div style="color:var(--text-secondary);font-size:.85rem">${processedCount} task${processedCount === 1 ? '' : 's'} processed this pass.</div>
      </div>
      <button class="btn btn-primary w-full" id="inbox-done-btn" style="justify-content:center;padding:12px">Done</button>`;
    content.querySelector('#inbox-done-btn').addEventListener('click', () => {
      closeSheet();
      if (rendered.has('plan')) { rendered.delete('plan'); renderTab('plan'); }
    });
  }

  function renderCurrent() {
    if (index >= queue.length) { renderDone(); return; }
    const task = queue[index];

    content.innerHTML = `
      <div class="sheet-title">Process inbox</div>
      <div style="font-size:.75rem;color:var(--text-tertiary);margin-top:-8px;margin-bottom:var(--space-3)">Task ${index + 1} of ${queue.length}</div>

      <div class="card" style="margin-bottom:var(--space-4)">
        <div style="font-weight:700;font-size:1rem;margin-bottom:6px">${esc(task.title)}</div>
        <div style="display:flex;gap:8px;align-items:center;font-size:.8rem;color:var(--text-secondary);flex-wrap:wrap">
          ${priorityBadge(task.priority)}
          ${task.estimatedHours ? `<span>${task.estimatedHours}h</span>` : ''}
          ${task.dueDate ? `<span>Due ${fmtDate(task.dueDate)}</span>` : ''}
        </div>
        ${task.description ? `<div style="margin-top:8px;font-size:.8rem;color:var(--text-secondary)">${esc(task.description)}</div>` : ''}
        ${task.notes ? `<div style="margin-top:6px;font-size:.75rem;color:var(--text-tertiary);font-style:italic">${esc(task.notes)}</div>` : ''}
      </div>

      <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:var(--space-2)">Assign a category</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-2);margin-bottom:var(--space-4)">
        ${cats.map(c => `
          <button class="btn btn-ghost inbox-cat-btn" data-cat-id="${esc(c.id)}" style="justify-content:flex-start;gap:8px;padding:10px">
            <span style="width:10px;height:10px;border-radius:50%;background:${esc(c.color)};flex-shrink:0"></span>
            <span style="font-size:.85rem">${esc(c.name)}</span>
          </button>`).join('')}
      </div>

      <div style="display:flex;gap:var(--space-3)">
        <button class="btn btn-ghost w-full" id="inbox-skip-btn" style="justify-content:center;padding:10px">Skip for now</button>
        <button class="btn btn-ghost w-full" id="inbox-done-task-btn" style="justify-content:center;padding:10px">Mark done</button>
      </div>`;

    content.querySelectorAll('.inbox-cat-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        await api.tasks.update(task.id, { categoryId: btn.dataset.catId }).catch(() => {});
        processedCount++;
        index++;
        renderCurrent();
      });
    });
    content.querySelector('#inbox-skip-btn').addEventListener('click', () => {
      index++;
      renderCurrent();
    });
    content.querySelector('#inbox-done-task-btn').addEventListener('click', async () => {
      await api.tasks.update(task.id, { status: 'done' }).catch(() => {});
      processedCount++;
      index++;
      renderCurrent();
    });
  }

  renderCurrent();
}

// ── PLANNING MODE (post-weekly-review) ────────────────────────────────────
// Triggered automatically right after saving a Weekly Review, and re-visitable
// later via Pulse's own banner (see pulse.js) if the week hasn't been marked
// planned yet. Shows unscheduled open tasks with a one-tap handoff into the
// Schedule Assistant, and a "week planned" completion action
// (`PATCH /api/weekly-reviews/:weekStart/plan`) so Pulse stops nudging once
// the user considers the week actually planned.
export async function openPlanningModeSheet() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  content.innerHTML = `<div class="sheet-title">Plan your week</div><p class="color-tertiary text-sm">Loading…</p>`;
  presentSheet();

  const { weekStart, weekEnd } = getWeekBounds();
  const [tasks, blocks] = await Promise.all([
    api.tasks.list().catch(() => []),
    api.blocks.list({ from: weekStart, to: weekEnd }).catch(() => []),
  ]);
  const scheduledTaskIds = new Set(blocks.map(b => b.taskId).filter(Boolean));
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const unscheduled = tasks
    .filter(t => (t.status === 'open' || t.status === 'in_progress') && !scheduledTaskIds.has(t.id))
    .sort((a, b) => (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1))
    .slice(0, 8);

  content.innerHTML = `
    <div class="sheet-title">Plan your week</div>
    <div class="field-helper" style="margin-top:-8px;margin-bottom:var(--space-4)">${fmtWeekRange(weekStart, weekEnd)}</div>

    ${unscheduled.length === 0 ? `
      <div style="text-align:center;padding:var(--space-6) 0">
        <div style="font-size:2rem;margin-bottom:var(--space-3)">✅</div>
        <div style="font-weight:700">You're all set for the week!</div>
        <div style="color:var(--text-secondary);font-size:.85rem;margin-top:4px">No unscheduled open tasks right now.</div>
      </div>
    ` : `
      <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:var(--space-2)">${unscheduled.length} task${unscheduled.length === 1 ? '' : 's'} need scheduling</div>
      ${unscheduled.map(t => `
        <div class="card" style="margin-bottom:var(--space-2);display:flex;align-items:center;justify-content:space-between;gap:var(--space-3)">
          <div style="min-width:0">
            <div style="font-weight:600;font-size:.85rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(t.title)}</div>
            <div style="display:flex;gap:6px;align-items:center;margin-top:2px">
              ${priorityBadge(t.priority)}
              ${t.dueDate ? `<span style="font-size:.7rem;color:var(--text-tertiary)">Due ${fmtDate(t.dueDate)}</span>` : ''}
            </div>
          </div>
          <button class="btn btn-ghost plan-schedule-btn" data-tid="${esc(t.id)}" style="flex-shrink:0;padding:8px 12px;font-size:.8rem">🗓 Schedule</button>
        </div>`).join('')}
    `}

    <button class="btn btn-primary w-full" id="plan-mark-planned-btn" style="justify-content:center;padding:12px;margin-top:var(--space-4)">✓ Mark week as planned</button>`;

  content.querySelectorAll('.plan-schedule-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const task = tasks.find(t => t.id === btn.dataset.tid);
      if (task) openScheduleAssistantSheet(task);
    });
  });

  content.querySelector('#plan-mark-planned-btn').addEventListener('click', async () => {
    await api.weeklyReviews.markPlanned(weekStart).catch(() => {});
    closeSheet();
    showToast('Week marked as planned');
    if (rendered.has('pulse')) { rendered.delete('pulse'); renderTab('pulse'); }
  });
}

// ── RE-EXPORT closeSheet so other modules can import it ───────────────────
// (utils.js already has it, but for convenience from app context)

// Expose sheet openers globally so other tab modules (like Pulse) can call them easily
window.openAddTaskSheet = openAddTaskSheet;
window.openAddBlockSheet = openAddBlockSheet;
window.openIncomeSheet = openIncomeSheet;
window.openExpenseSheet = openExpenseSheet;
window.openCommissionSheet = openCommissionSheet;
window.openTradeSheet = openTradeSheet;
window.openTradeDetailSheet = openTradeDetailSheet;
window.openRothSheet = openRothSheet;
window.openWeeklyReviewSheet = openWeeklyReviewSheet;
window.openScheduleAssistantSheet = openScheduleAssistantSheet;
window.openInboxProcessingSheet = openInboxProcessingSheet;
window.openPlanningModeSheet = openPlanningModeSheet;
window.switchTab = switchTab;

async function boot() {
  // A reset link was opened — show the "set new password" view regardless of
  // any existing session, and don't auto-route into the app.
  if (new URLSearchParams(location.search).get('reset_token')) {
    showAuthMode('reset-set');
    return;
  }
  try {
    const session = await api.auth.session();
    if (session?.user) {
      await afterAuth();
    }
  } catch {
    // no session — auth screen stays
  }
}

boot();
